/**
 * headerFooterActionExecutors — LE verbe d'en-tête et de pied de page du canvas.
 *
 * > Un en-tête d'acte porte trois choses que l'avocat écrit à la main sur
 * > chaque écriture : la ligne du cabinet, la référence du dossier (« RG n°
 * > 24/01234 »), et l'objet (« CONCLUSIONS RÉCAPITULATIVES »). L'agent savait
 * > écrire le corps, le mettre en forme, l'annoter et le sourcer ; il ne savait
 * > pas toucher au bandeau qui coiffe chaque page. `set_header_footer`.
 *
 * Les sept temps sont ceux de `footnoteActionExecutors` : résoudre la cible,
 * instantané **pre**, gardes de capacité, dériver l'adresse, appels mutants
 * sous `muterSousFences`, instantané **post** + `verifierEpoque`, receipt dont
 * la postcondition est une RELECTURE.
 *
 * ## Ce qui est MESURÉ, et ce que ça change (relevé du 2026-09-05)
 *
 * `tests/ws-mocked/mesure-en-tetes-et-pieds.spec.ts`, §10 de
 * `docs/features/redaction-web-canvas/superdoc-document-api.md`.
 *
 * 1. **Écrire par le locator de story `headerFooterSlot` matérialise tout seul
 *    la partie manquante — et c'est précisément le piège.** La matérialisation
 *    AVANCE la révision du document, et le verrou optimiste est contrôlé
 *    APRÈS : un `create.paragraph` qui présente la révision qu'il vient de lire
 *    est donc refusé par construction (`REVISION_MISMATCH — expected-revision 0
 *    does not match current revision 1`), pendant que la matérialisation, elle,
 *    a bien eu lieu. Le premier en-tête d'un document n'était jamais écrit, sur
 *    un refus qui accuse un conflit inexistant. Ce module matérialise donc À
 *    DÉCOUVERT (`parts.create` + `refs.set`) quand le slot n'est pas déjà
 *    `explicit`, ce qui rend chaque révision attendue relisible entre deux
 *    appels. Ces deux opérations restent HORS des `requiredOperations` : elles
 *    ne servent qu'à ce cas, et les déclarer ferait disparaître le verbe sur un
 *    document dont l'en-tête existe déjà.
 *    ⚠️ `sourceRefId` est passé sur un slot HÉRITÉ : sans lui, la copie locale
 *    naîtrait vide et l'avocat perdrait l'en-tête que la section précédente lui
 *    donnait.
 * 2. **Une adresse de bloc SANS sa story vise le corps du document.** Mesuré
 *    des deux côtés : `format.paragraph.setAlignment` sur le `nodeId` nu rend
 *    `TARGET_NOT_FOUND — Block "32920A4E" not found.`, et réussit avec la
 *    story. Même piège que celui qui a fait perdre l'alignement du papier à
 *    en-tête le 2026-08-24 (`canvasLetterhead.ts`).
 * 3. ⚠️ **Un paragraphe qui ne porte QU'UNE image se relit `text:''` /
 *    `isEmpty:true`** — donc exactement comme une ligne blanche. Or le papier à
 *    en-tête du cabinet vit là. Une suppression « des blocs vides » effacerait
 *    le logo de l'avocat sans que rien ne le signale, et l'agent ne pourrait pas
 *    le reposer : il n'a pas les octets. D'où la règle dure de ce module — voir
 *    `blocsSupprimables`.
 * 4. **Écrire dans un en-tête ne produit AUCUNE révision suivie.** Sous fenêtre
 *    `suggesting`, `create.paragraph` dans une story d'en-tête rend pourtant un
 *    `trackedChangeRefs` dans sa réponse, et `trackChanges.list()` n'en compte
 *    aucune de plus — pendant que le témoin posé dans le CORPS en produit une.
 *    Cinquième démenti du `tracked` annoncé. D'où `reviewPolicy:'direct'` au
 *    catalogue, le `changeMode:'direct'` EN DUR ici, et la phrase de la
 *    description du verbe qui prévient l'agent que l'avocat n'aura rien à
 *    relire.
 * 5. **`sections.list()` OMET `titlePage` tant qu'il n'a jamais été levé**,
 *    alors qu'il publie `oddEvenHeadersFooters: false`. On ne lit donc pas ce
 *    drapeau pour décider : on le POSE, et `NO_OP` dit qu'il était déjà là.
 * 6. **La première page distincte et les pages paires sont des réglages de
 *    SECTION : ils valent pour l'en-tête ET pour le pied** (relevé du
 *    2026-10-05, `tests/ws-mocked/mesure-premiere-page-distincte.spec.ts`). Sur
 *    le gabarit Hector, `sections.setTitlePage` seul laisse `footer:first` à
 *    `none` : la page 1 ne peint plus le pied de pagination, et le fichier
 *    exporté porte `w:titlePg` sans pied de première page. Même fait pour
 *    `setOddEvenHeadersFooters` et les pages paires. `parts.create({kind,
 *    sourceRefId})` copie la partie source avec ses champs `PAGE` / `NUMPAGES`
 *    et son alignement (fichier exporté et page peinte relus).
 *
 * ## Une écriture sur l'en-tête ne change pas ce que montre le pied, et inversement
 *
 * Règle de produit. Quand cet appel lève un des deux réglages, la partie non
 * demandée reçoit, dans la zone que le réglage vient d'activer, une copie de sa
 * zone `primary` : les pages concernées montrent ce qu'elles montraient
 * (`garderLAutrePartie`). Un réglage déjà levé (`NO_OP`) ne copie rien : ces
 * pages ne montraient déjà plus la zone `primary`. « Gardée par copie » ne se
 * dit qu'après comparaison de la copie à sa source dans le fichier exporté
 * (`copieDePartie.ts`). Ce qui n'a pas pu être tenu ou vérifié est dit par le
 * reçu (`headerFooter.other_part`, statut `partial`), jamais réparé en
 * devinant : une zone que l'autre partie portait déjà, masquée tant que le
 * réglage était éteint, n'est ni écrasée ni copiée.
 *
 * ## Un refus en cours de route ne défait rien, et le dit
 *
 * L'appel fait jusqu'à huit écritures, une entrée d'historique chacune (relevé
 * du 2026-10-05, `mesure-annulation-entete-et-pied.spec.ts`). Si l'éditeur en
 * refuse une après que d'autres ont abouti, le reçu est `partial` et dit ce qui
 * reste dans le document (`refuser`) ; rien n'est annulé automatiquement. Ce
 * que laisse une annulation au compte trop court : `pagesSansLeurPartie.ts`.
 *
 * Le reçu compte ses écritures (`headerFooter.writes`, comme
 * `pageLayout.writes`) et dit la phrase d'annulation avec ce compte : c'est lui
 * que l'agent passe à `undo_changes`, pas un compte deviné.
 *
 * ## Le numéro de page : du contenu de la zone, jugé au fichier
 *
 * Un paragraphe dont le texte porte `{page}` ou `{pages}` est écrit sans ces
 * jetons, puis reçoit ses champs `PAGE` / `NUMPAGES` (`fields.insert`), du plus
 * loin au plus près. `remove_page_number` retire les champs de la zone, puis le
 * paragraphe qui les portait — sauf s'il porte une image —, après avoir posé un
 * paragraphe vide quand il était le seul de la partie. Les mesures de
 * l'éditeur qui commandent cet ordre sont dans l'EN-TÊTE de `numeroDePage.ts`,
 * seul endroit qui les porte.
 *
 * La règle du retrait est la LIGNE entière : un texte qui partage la ligne du
 * numéro (« Cabinet X, page 3 ») part avec lui. Le reçu ne dit donc pas « le
 * reste de la zone est gardé » : il cite le texte parti (`effetDuRetrait`,
 * `page_numbers.removed_paragraphs`), et la ligne du verbe dit au modèle de
 * réécrire la zone quand ce texte doit rester. Le statut suit le FICHIER : un
 * paragraphe gardé sans son champ (il porte une image) est un numéro retiré,
 * `ok` ; `HEADER_FOOTER_PAGE_NUMBER_NOT_REMOVED` ne vaut que si un champ reste.
 *
 * L'éditeur ne rend un champ par aucune de ses lectures de texte : ce que la
 * zone porte après l'appel est relu dans le fichier exporté
 * (`lecteurDeForme.ts`, `numerosDePage`) et dit par `headerFooter.page_numbers`
 * — conforme, différent ou non lisible, comme une écriture de forme. Un export
 * de plus par appel, et seulement quand l'appel pose ou retire un numéro.
 *
 * Supprimer un bloc qui porte des champs (`replace` sur un pied de pagination)
 * coûte aussi un export, un seul par appel : c'est au fichier que se lit la
 * partie de la zone visée, donc lesquels des champs publiés sous l'identifiant
 * du bloc sont les siens (`storyIdDesChampsDuBloc`).
 */

import {
  CANVAS_HEADER_FOOTER_BLOCKS_MAX,
  CANVAS_PARAGRAPH_FIELDS_MAX,
  CANVAS_REMOVED_PARAGRAPHS_MAX,
  type CanvasActionInput,
  type CanvasHeaderFooterOtherPart,
  type CanvasHeaderFooterPageNumbers,
  type CanvasHeaderFooterType,
  type CanvasPageNumberField,
} from '@hector/shared-types';
import {
  abortedBeforeMutation,
  avecUnConstat,
  buildVerifiedReceipt,
  partialAfterMutation,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type CanvasHeaderFooterReceipt,
  type VerificationCheck,
} from './actionReceipt';
import {
  familleDuDocument,
  idsDeRevision,
  lireLeStatutDeSlot,
  operationDuDocument,
  slotDEnTete,
  storyDuSlot,
  type CanvasSnapshot,
} from './canvasSnapshot';
import { comparerLaCopieALaSource } from './copieDePartie';
import { relireLeFichierDeLActe } from './ecritureDeForme';
import { exporterLActeEnMemoire } from './exportDeLActe';
import { muterSousFences, revisionCourante } from './fencesDeMutation';
import { numeroDePageDe, type FormeHorsDuCorps } from '@hector/docx-forme';
import { INSTRUCTION_DU_NUMERO, jugerLaPose, jugerLeRetrait, lireLesJetons, ordreDePose, type ParagrapheRelu } from './numeroDePage';
import {
  adresseDeSection,
  chaine,
  entrees,
  isRecord,
  refusDeCapacite as refusDeCapaciteDeLEditeur,
  refusDuFournisseur,
  type AdresseDeSection,
} from './reponsesDeLEditeur';
import { snapshot, type CanvasActionContext } from './textActionExecutors';

export type SetHeaderFooterInput = Extract<CanvasActionInput, { action: 'set_header_footer' }>;

type MethodeProvider = (input?: unknown, options?: unknown) => unknown;

/** Les deux parties d'une section : l'en-tête et le pied. */
type PartieDEnTete = 'header' | 'footer';

type Revision = Awaited<ReturnType<typeof revisionCourante>>;

/**
 * Une écriture de l'appel : relit la révision à présenter, ouvre la fence, et passe à l'opération les options
 * (`changeMode:'direct'`, révision attendue) — voir `prochaineRevision` dans `executeSetHeaderFooter`.
 */
type Ecrire = (
  operation: string,
  appel: (options: { changeMode: 'direct'; expectedRevision: Revision }) => unknown,
) => Promise<unknown>;

/**
 * Les surfaces du Document API que ce verbe emprunte, toutes MESURÉES présentes
 * sur `superdoc@2.6.0` (relevé du 2026-09-05).
 *
 * Même patron que `SurfacesDeMiseEnForme` : le contexte partagé ne déclare que
 * ce dont les verbes de texte ont besoin, et chaque famille de verbes ajoute la
 * sienne — une déclaration unique et exhaustive du fournisseur ferait passer
 * pour vivants des chemins que personne n'emprunte.
 *
 * ⚠️ `headerFooters.parts.create` et `headerFooters.refs.set` n'y sont
 * empruntées QUE lorsque la zone visée n'a pas encore sa propre partie — d'où
 * leur absence des `requiredOperations` du catalogue, et un refus NOMMÉ à
 * l'exécution dans ce seul cas.
 */
interface SurfacesDEnTete {
  sections?: {
    list?: MethodeProvider;
    setTitlePage?: MethodeProvider;
    setOddEvenHeadersFooters?: MethodeProvider;
  };
  headerFooters?: {
    resolve?: MethodeProvider;
    parts?: { create?: MethodeProvider };
    refs?: { set?: MethodeProvider };
  };
  create?: { paragraph?: MethodeProvider };
  format?: { paragraph?: { setAlignment?: MethodeProvider } };
  blocks?: { delete?: MethodeProvider };
  /** La SEULE surface qui distingue un logo d'une ligne blanche — best-effort. */
  images?: { list?: MethodeProvider };
  /** Les champs de numéro de page : exigés seulement par un appel qui en pose ou en retire. */
  fields?: { insert?: MethodeProvider; list?: MethodeProvider; remove?: MethodeProvider };
}

type ContexteDEnTete = CanvasActionContext & { doc: CanvasActionContext['doc'] & SurfacesDEnTete };

/**
 * Borne des extraits rendus au modèle, points de suspension COMPRIS : c'est aussi
 * la borne du contrat (`parts_after.*.text`), et un caractère de plus fait refuser
 * le reçu entier.
 */
const MAX_EXTRAIT = 200;

/** Ce que les libellés d'erreur disent en français à propos de la cible visée. */
const NOM_DE_CIBLE: Readonly<Record<PartieDEnTete, string>> = {
  header: "l'en-tête",
  footer: 'le pied de page',
};

/** Ce que les libellés d'erreur disent en français à propos de la zone visée. */
const NOM_DE_ZONE: Readonly<Record<CanvasHeaderFooterType, string>> = {
  primary: 'de toutes les pages',
  first_page: 'de la première page',
  even_pages: 'des pages paires',
};

function extrait(texte: string): string {
  const compact = texte.replace(/\s+/gu, ' ').trim();
  return compact.length > MAX_EXTRAIT ? `${compact.slice(0, MAX_EXTRAIT - 1)}…` : compact;
}

/** `blocks.list` rend `{ blocks: [...] }` — jamais `items`, mesuré. */
function blocsDe(source: unknown): Record<string, unknown>[] {
  if (!isRecord(source) || !Array.isArray(source.blocks)) return [];
  return source.blocks.filter(isRecord);
}

function receiptEstEnSucces(valeur: unknown): boolean {
  return isRecord(valeur) && valeur.success === true;
}

/**
 * Code d'échec d'un reçu, ou `null` en succès.
 *
 * `NO_OP` en fait partie — le Document API classe « rien à faire » comme un
 * échec. C'est lui qui dit que le drapeau de première page était DÉJÀ levé, et
 * le journaliser comme une anomalie rendrait le cas le plus fréquent le plus
 * bruyant (même arbitrage que `canvasLetterhead.lireLeCodeDEchec`).
 */
function codeDEchec(valeur: unknown): string | null {
  if (receiptEstEnSucces(valeur)) return null;
  if (!isRecord(valeur)) return 'REPONSE_ILLISIBLE';
  const echec = isRecord(valeur.failure) ? valeur.failure : null;
  return chaine(echec?.code) ?? 'ECHEC_SANS_CODE';
}

/**
 * La résolution d'un slot : son statut ET son `refId`.
 *
 * Le `refId` ne sert que sur un slot `inherited` — c'est la partie à CLONER
 * (`parts.create({sourceRefId})`) pour matérialiser une copie locale sans perdre
 * ce que la section précédente y avait mis. Sur un `explicit`, la partie est
 * déjà là ; sur un `none`, il n'y a rien à cloner.
 */
function lireLaResolution(valeur: unknown): { statut: 'explicit' | 'inherited' | 'none'; refId?: string } {
  const statut = lireLeStatutDeSlot(valeur);
  const refId = isRecord(valeur) ? chaine(valeur.refId) : undefined;
  return { statut, ...(refId === undefined ? {} : { refId }) };
}

/**
 * L'adresse ÉCRIVABLE d'un bloc de story.
 *
 * ⚠️ **La story fait partie de l'adresse, et son absence ne rend pas une
 * erreur de forme : elle vise le CORPS.** Mesuré des deux côtés le 2026-09-05.
 * C'est le même défaut qui a fait perdre l'alignement du papier à en-tête le
 * 2026-08-24, et il est silencieux : le reçu revient `success:false` avec
 * `TARGET_NOT_FOUND`, jamais une exception.
 */
function adresseDeBloc(
  bloc: Record<string, unknown>,
  story: ReturnType<typeof storyDuSlot>,
): { kind: 'block'; nodeType: string; nodeId: string; story: ReturnType<typeof storyDuSlot> } | null {
  const nodeId = chaine(bloc.nodeId);
  if (nodeId === undefined) return null;
  return { kind: 'block', nodeType: chaine(bloc.nodeType) ?? 'paragraph', nodeId, story };
}

/**
 * Les `blockId` de la story qui portent une image, d'après `images.list`.
 *
 * `fiable` dit si la question a reçu une VRAIE réponse. Un ensemble vide veut
 * dire deux choses opposées — « aucune image » ou « je n'ai pas pu regarder » —
 * et c'est cette distinction qui décide plus bas si l'on ose retirer un bloc
 * dont le texte est vide. Sans elle, un éditeur muet et un en-tête réellement
 * nu se traitent pareil, et la règle prudente s'applique à tort dans le cas
 * fréquent.
 */
async function blocsPorteursDImage(
  ctx: ContexteDEnTete,
  story: ReturnType<typeof storyDuSlot>,
): Promise<{ porteurs: Set<string>; fiable: boolean }> {
  const porteurs = new Set<string>();
  // ⚠️ `doc.images` est un proxy APPELABLE (`typeof === 'function'`) : un
  // `isRecord` le rejetterait en silence — voir `familleDuDocument`.
  const list = operationDuDocument(ctx.doc, 'images', 'list');
  if (list === undefined) return { porteurs, fiable: false };
  try {
    for (const entree of entrees(await list({ in: story }))) {
      const ancre = isRecord(entree.anchorRef) ? entree.anchorRef : undefined;
      const blockId = chaine(ancre?.blockId);
      if (blockId !== undefined) porteurs.add(blockId);
    }
  } catch {
    // Une lecture d'images en échec ne doit pas empêcher d'écrire l'en-tête. On
    // rend l'ensemble vide ET `fiable: false` : c'est alors la garde du texte
    // non vide qui protège seule le papier à en-tête du cabinet.
    return { porteurs, fiable: false };
  }
  return { porteurs, fiable: true };
}

/**
 * Les blocs que l'on s'autorise à retirer, et c'est la règle DURE du module.
 *
 * Un bloc n'est supprimable que s'il réunit trois conditions :
 *  1. **il n'est pas de nous** — les paragraphes qu'on vient d'écrire restent ;
 *  2. **il ne porte pas d'image** (`images.list`) ;
 *  3. **son texte n'est pas vide, OU l'on sait de source sûre qu'il ne porte
 *     pas d'image** — un paragraphe qui ne contient qu'une image se relit
 *     `text:''` (mesuré 2026-09-05). Quand `images.list` a répondu
 *     (`listeDImagesFiable`), son silence sur un bloc VAUT « pas d'image » et
 *     un `replace` retire ce bloc. Quand elle n'a pas pu répondre, la garde du
 *     texte non vide protège seule le papier à en-tête du cabinet et on garde
 *     tout : une ligne blanche ne coûte qu'un interligne, un logo effacé est
 *     irrécupérable — l'agent n'a pas les octets pour le reposer.
 *
 *     ⚠️ **Ce qu'un pied de pagination rend à la lecture** (relevé du 2026-10-05,
 *     `mesure-premiere-page-distincte.spec.ts`, gabarit `blank-with-page-numbers`) :
 *     `blocks.list` rend `text:'Page  / '`, `isEmpty:false` — les libellés
 *     littéraux passent, les champs PAGE et NUMPAGES non. Un tel bloc est donc
 *     supprimable par la garde du texte : la pagination d'un gabarit ne résiste
 *     pas à `replace`.
 *
 * L'exception historique reste : le paragraphe vide de TÊTE d'une partie que
 * NOUS venons de faire naître (`partieNeuve`) part toujours, même sans réponse
 * de `images.list` — on sait qu'il n'était pas là avant, et `parts.create`
 * comme la matérialisation en fabriquent exactement un (mesuré). Le laisser
 * mettrait une ligne blanche au-dessus de la mention du cabinet, à chaque page.
 */
function blocsSupprimables(input: {
  blocs: readonly Record<string, unknown>[];
  nodeIdsEcrits: ReadonlySet<string>;
  porteursDImage: ReadonlySet<string>;
  listeDImagesFiable: boolean;
  mode: 'replace' | 'append';
  partieNeuve: boolean;
}): Record<string, unknown>[] {
  return input.blocs.filter((bloc, rang) => {
    const nodeId = chaine(bloc.nodeId);
    if (nodeId === undefined || input.nodeIdsEcrits.has(nodeId)) return false;
    if (input.porteursDImage.has(nodeId)) return false;
    // `blocks.delete` refuse de toute façon un bloc `image` (DeletableBlockNodeType) :
    // l'écarter ici évite un refus bruyant relayé à l'avocat comme un incident.
    if (chaine(bloc.nodeType) === 'image') return false;
    const texte = (chaine(bloc.text) ?? chaine(bloc.textPreview) ?? '').trim();
    if (texte.length > 0) return input.mode === 'replace';
    // Bloc vide : l'artefact de tête d'une partie qu'on vient de créer part
    // toujours ; sinon il ne part qu'en `replace`, et seulement si `images.list`
    // a bien répondu qu'aucune image n'y est ancrée.
    if (input.partieNeuve && rang === 0) return true;
    return input.mode === 'replace' && input.listeDImagesFiable;
  });
}


/** Les pages qu'une zone couvre, pour les phrases rendues à l'agent. */
const PAGES_DE_ZONE: Readonly<Record<CanvasHeaderFooterType, string>> = {
  primary: 'les pages sans zone à elles',
  first_page: 'la première page',
  even_pages: 'les pages paires',
};

/**
 * Ce que `headerFooter.parts_after` rend, dit au modèle selon la zone écrite : l'en-tête et le pied, chacun
 * pour la première page et pour les autres pages. Les zones de pages paires n'y sont pas (choix du
 * 2026-10-05 : dire ce qui est rendu plutôt qu'élargir la forme) ; `canvas_inspect` les donne.
 */
function ceQueRendentLesPartiesRelues(type: CanvasHeaderFooterType): string {
  return "headerFooter.parts_after donne, relus, l'en-tête et le pied de la première page et des autres pages"
    + (type === 'even_pages'
      ? ' ; les zones de pages paires n\'y figurent pas : relis-les par canvas_inspect avec includeDomains contenant "headersFooters".'
      : '.');
}

/** Une zone telle que la section la résout, avec l'extrait de son texte. `none` n'est jamais lu : lire matérialiserait. */
async function lireUnePartie(
  ctx: ContexteDEnTete,
  resoudreLeSlot: MethodeProvider,
  section: AdresseDeSection,
  cible: PartieDEnTete,
  zone: CanvasHeaderFooterType,
): Promise<{ statut: 'explicit' | 'inherited' | 'none'; refId?: string; texte: string }> {
  const resolution = lireLaResolution(await resoudreLeSlot({ target: slotDEnTete(section, cible, zone) }));
  if (resolution.statut === 'none') return { ...resolution, texte: '' };
  const blocs = blocsDe(await ctx.doc.blocks?.list?.({ in: storyDuSlot(section, cible, zone), includeText: true, offset: 0, limit: CANVAS_HEADER_FOOTER_BLOCKS_MAX }));
  const texte = blocs.map((bloc) => (chaine(bloc.text) ?? chaine(bloc.textPreview) ?? '').trim()).filter((ligne) => ligne.length > 0).join('\n');
  return { ...resolution, texte: extrait(texte) };
}

/** Ce que la partie non demandée portait avant le réglage : sa zone `primary`, et la zone que le réglage activera. */
interface AutrePartieAvant {
  partie: PartieDEnTete;
  primaire: Awaited<ReturnType<typeof lireUnePartie>>;
  zone: Awaited<ReturnType<typeof lireUnePartie>>;
}

/**
 * Le sort de la partie non demandée, tel que l'exécuteur le décide : la forme du
 * contrat (`CanvasHeaderFooterOtherPart`), où la raison est due dès que la partie a changé.
 */
type SortDeLAutrePartie =
  | { part: PartieDEnTete; outcome: 'untouched' }
  | { part: PartieDEnTete; outcome: 'kept_by_copy' }
  | { part: PartieDEnTete; outcome: 'copy_unverified'; reason: 'source_has_image' | 'source_not_comparable' | 'file_unreadable' }
  | {
      part: PartieDEnTete;
      outcome: 'changed';
      reason: 'copy_unavailable' | 'copy_refused' | 'zone_already_present' | 'copy_differs';
      /** Les écarts relevés au fichier quand la copie diffère de sa source. Dit par le message, pas par le contrat. */
      ecarts?: readonly string[];
    };

/** Le sort tel que le contrat le porte : sans ce qui ne sert qu'au message. */
function sortAuContrat(sort: SortDeLAutrePartie): CanvasHeaderFooterOtherPart {
  return sort.outcome === 'changed' || sort.outcome === 'copy_unverified'
    ? { part: sort.part, outcome: sort.outcome, reason: sort.reason }
    : { part: sort.part, outcome: sort.outcome };
}

/**
 * Garde à la partie non demandée ce qu'elle montrait, une fois le réglage de
 * section LEVÉ par cet appel : la zone que le réglage vient d'activer reçoit une
 * copie de la zone `primary`.
 *
 * Une copie, pas un second rattachement de la même partie : écrire plus tard le
 * pied de la première page ne doit pas réécrire celui des autres pages.
 */
async function garderLAutrePartie(
  ctx: ContexteDEnTete,
  entree: {
    section: AdresseDeSection;
    zone: CanvasHeaderFooterType;
    avant: AutrePartieAvant;
    resoudreLeSlot: MethodeProvider;
    ecrire: Ecrire;
  },
): Promise<SortDeLAutrePartie> {
  const { section, zone, avant } = entree;
  const partie = avant.partie;
  // La zone existait déjà, masquée tant que le réglage était éteint : le réglage l'affiche. Ni écrasée ni copiée.
  if (avant.zone.statut !== 'none') return { part: partie, outcome: 'changed', reason: 'zone_already_present' };
  // Rien n'était montré, rien n'est montré.
  if (avant.primaire.statut === 'none') return { part: partie, outcome: 'untouched' };

  const enTetes = familleDuDocument(ctx.doc, 'headerFooters');
  const creerLaPartie = operationDuDocument(enTetes, 'parts', 'create');
  const poserLaReference = operationDuDocument(enTetes, 'refs', 'set');
  if (creerLaPartie === undefined || poserLaReference === undefined || avant.primaire.refId === undefined) {
    return { part: partie, outcome: 'changed', reason: 'copy_unavailable' };
  }
  const refuse: SortDeLAutrePartie = { part: partie, outcome: 'changed', reason: 'copy_refused' };
  const copie = await entree.ecrire(
    'headerFooters.parts.create',
    () => creerLaPartie({ kind: partie, sourceRefId: avant.primaire.refId }, { changeMode: 'direct' }),
  );
  const refId = isRecord(copie) ? chaine(copie.refId) : undefined;
  if (!receiptEstEnSucces(copie) || refId === undefined) return refuse;
  const slot = slotDEnTete(section, partie, zone);
  const rattachement = await entree.ecrire(
    'headerFooters.refs.set',
    () => poserLaReference({ target: slot, refId }, { changeMode: 'direct' }),
  );
  if (!receiptEstEnSucces(rattachement)) return refuse;
  // Première preuve : le slot se relit rattaché. L'accusé du rattachement ne suffit pas.
  if (lireLeStatutDeSlot(await entree.resoudreLeSlot({ target: slot })) !== 'explicit') return refuse;
  // Seconde preuve : le CONTENU. Les lectures de l'éditeur ne rendent ni les champs ni l'alignement d'une
  // partie ; le fichier exporté les porte (`copieDePartie.ts`). Un export par appel, et seulement quand il a copié.
  let octets: Uint8Array;
  try {
    octets = await exporterLActeEnMemoire(ctx.doc);
  } catch {
    return { part: partie, outcome: 'copy_unverified', reason: 'file_unreadable' };
  }
  const verdict = await comparerLaCopieALaSource(octets, { source: avant.primaire.refId, copie: refId });
  if (verdict.verdict === 'identique') return { part: partie, outcome: 'kept_by_copy' };
  return verdict.verdict === 'differente'
    ? { part: partie, outcome: 'changed', reason: 'copy_differs', ecarts: verdict.ecarts }
    : { part: partie, outcome: 'copy_unverified', reason: verdict.raison };
}

/** L'erreur d'un reçu dont la partie non demandée a changé : elle nomme le champ, ce qui était montré et ce qui l'est. */
function erreurDAutrePartieChangee(
  input: SetHeaderFooterInput,
  sort: Extract<SortDeLAutrePartie, { outcome: 'changed' }>,
  avant: AutrePartieAvant,
): CanvasActionReceipt['errors'][number] {
  const cause = sort.reason === 'zone_already_present'
    ? `${NOM_DE_CIBLE[sort.part]} portait déjà une zone ${NOM_DE_ZONE[input.type]}, masquée jusqu'ici, que le réglage affiche `
      + `désormais (${avant.zone.texte.length === 0 ? 'sans texte lisible' : `« ${avant.zone.texte} »`}) ; elle n'a été ni écrasée ni copiée`
    : sort.reason === 'copy_unavailable'
      ? `cet éditeur n'expose pas la copie d'une partie, donc ${NOM_DE_CIBLE[sort.part]} n'y est plus montré`
      : sort.reason === 'copy_differs'
        ? `la copie faite pour ces pages, relue dans le fichier de l'acte, diffère de sa source `
          + `(${(sort.ecarts ?? []).join(', ')}), donc ${NOM_DE_CIBLE[sort.part]} n'y montre plus la même chose`
        : `l'éditeur a refusé la copie, donc ${NOM_DE_CIBLE[sort.part]} n'y est plus montré`;
  return {
    code: 'HEADER_FOOTER_OTHER_PART_CHANGED',
    message: `set_header_footer a écrit ${NOM_DE_CIBLE[input.target]} ${NOM_DE_ZONE[input.type]} comme demandé, mais `
      + `${NOM_DE_CIBLE[sort.part]} a changé sur ${PAGES_DE_ZONE[input.type]} sans que ce soit demandé `
      + `(headerFooter.other_part, raison « ${sort.reason} »). Avant l'écriture, ${NOM_DE_CIBLE[sort.part]} y montrait `
      + `${avant.primaire.texte.length === 0 ? 'une zone sans texte lisible' : `« ${avant.primaire.texte} »`} — un numéro de page ne se relit `
      + `pas en texte ; ${cause}. ${ceQueRendentLesPartiesRelues(input.type)} Ne rejoue pas l'appel : `
      + "l'écriture demandée est faite. Dis à l'avocat ce qui a changé ; s'il veut y retrouver un numéro de page, écris-le "
      + 'dans cette zone par ce verbe ({page}, {pages}).',
  };
}

/** Pourquoi une copie faite n'a pas pu être comparée à sa source, dit au modèle. */
const POURQUOI_NON_VERIFIEE: Readonly<Record<Extract<SortDeLAutrePartie, { outcome: 'copy_unverified' }>['reason'], string>> = {
  source_has_image: 'la source porte une image, que la relecture du fichier ne sait pas comparer',
  source_not_comparable: 'la source porte un tableau, une zone de texte ou un contrôle de contenu, que la relecture du fichier ne sait pas comparer',
  file_unreadable: "le fichier de l'acte n'a pas pu être exporté, ou une des deux parties n'y a pas été retrouvée",
};

/** L'erreur d'un reçu dont l'autre partie a été copiée sans que la copie ait pu être vérifiée. */
function erreurDeCopieNonVerifiee(
  input: SetHeaderFooterInput,
  sort: Extract<SortDeLAutrePartie, { outcome: 'copy_unverified' }>,
): CanvasActionReceipt['errors'][number] {
  return {
    code: 'HEADER_FOOTER_OTHER_PART_UNVERIFIED',
    message: `set_header_footer a écrit ${NOM_DE_CIBLE[input.target]} ${NOM_DE_ZONE[input.type]} comme demandé. `
      + `${NOM_DE_CIBLE[sort.part]} ${NOM_DE_ZONE[input.type]} a reçu une copie de celui des autres pages, mais cette copie `
      + `n'est PAS vérifiée (headerFooter.other_part, raison « ${sort.reason} ») : ${POURQUOI_NON_VERIFIEE[sort.reason]}. `
      + `N'annonce pas à l'avocat que ${NOM_DE_CIBLE[sort.part]} est inchangé sur ${PAGES_DE_ZONE[input.type]} : dis-lui de le `
      + "vérifier à l'écran. Ne rejoue pas l'appel : l'écriture demandée est faite.",
  };
}

/** Un bloc que l'appel n'a pas pu supprimer : le refus de l'éditeur, à quelle étape, et ce que le bloc a déjà perdu. */
interface EchecDeSuppression {
  code: string;
  message: string;
  etape: 'le retrait de ses champs' | 'sa suppression';
  /** Champs du bloc retirés avant le refus : ils ne reviennent pas. */
  champsDejaRetires: number;
}

/** La zone visée, relue au fichier exporté ; ou la cause, dite au modèle, pour laquelle elle ne l'est pas. */
type ZoneRelueAuFichier = { zone: FormeHorsDuCorps } | { cause: string };

/** L'échec d'une suppression de bloc, dit dans un avertissement : l'étape, le refus rendu tel quel, et les champs déjà partis. */
function direLEchecDeSuppression(echec: EchecDeSuppression): string {
  return `l'éditeur a refusé ${echec.etape}, code ${echec.code} : « ${echec.message} »`
    + (echec.champsDejaRetires === 0 ? '' : ` ; ${echec.champsDejaRetires} champ(s) de ce paragraphe sont déjà retirés et n'y reviennent pas — numéro de page compris s'il en portait un`);
}

/** Comment défaire l'appel, dit au modèle : la phrase de `set_page_layout`, sur le compte du reçu (`headerFooter.writes`). */
function pourAnnuler(ecritures: number): string {
  return `Pour annuler : undo_changes avec steps = ${ecritures}${ecritures === 0 ? ' — rien à annuler.' : '.'}`;
}

/** Le refus de capacité du verbe, pour chacune de ses surfaces (`reponsesDeLEditeur.ts`). */
function refusDeCapacite(resume: CanvasSnapshot, surface: string, contournement: string): CanvasActionReceipt {
  return refusDeCapaciteDeLEditeur({ intent: 'set_header_footer', resume, surface, contournement });
}

/**
 * Écrit dans l'en-tête ou le pied de page d'une section.
 *
 * ⚠️ **ÉCRIRE PUIS SUPPRIMER, jamais l'inverse** — même doctrine que
 * `write_markdown` et `replace_paragraph`, et ici elle porte double : une
 * écriture refusée n'entraîne aucune suppression (l'en-tête de l'avocat n'est
 * jamais vidé « pour rien »), ET c'est la première écriture qui MATÉRIALISE la
 * partie locale quand la zone est héritée d'une section antérieure. Supprimer
 * d'abord viserait des blocs qui appartiennent encore à la section précédente.
 */
export async function executeSetHeaderFooter(
  ctx: ContexteDEnTete,
  input: SetHeaderFooterInput,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);
  // ⚠️ **`sections`, `headerFooters` et `images` sont des proxys APPELABLES**
  // (`typeof === 'function'`), là où `footnotes`, `comments` ou `blocks` sont
  // des objets — mesuré le 2026-09-05, sans règle devinable. Une garde
  // `isRecord` les rejetterait en silence et ce verbe se déclarerait
  // indisponible sur tous les documents. `familleDuDocument` est la seule
  // lecture de famille autorisée.
  const listerLesSections = operationDuDocument(ctx.doc, 'sections', 'list');
  const resoudreLeSlot = operationDuDocument(ctx.doc, 'headerFooters', 'resolve');

  if (listerLesSections === undefined) {
    return refusDeCapacite(pre, 'sections.list',
      "Sans elle, aucune section ne peut être adressée, donc aucun en-tête. Écris la mention dans le CORPS de l'acte "
      + "si elle doit absolument y figurer, et dis à l'avocat qu'elle n'est pas dans l'en-tête.");
  }
  if (resoudreLeSlot === undefined) {
    return refusDeCapacite(pre, 'headerFooters.resolve',
      "C'est la seule surface qui dise si cette zone porte déjà quelque chose ; sans elle, écrire reviendrait à "
      + "poser un second en-tête par-dessus celui du cabinet sans le savoir.");
  }
  if (typeof ctx.doc.create?.paragraph !== 'function') {
    return refusDeCapacite(pre, 'create.paragraph',
      "C'est la seule surface mesurée capable d'écrire dans une story d'en-tête. Dis la mention à l'avocat dans la "
      + 'conversation plutôt que de la poser dans le corps de l\'acte, où elle serait fausse.');
  }
  if (typeof ctx.doc.blocks?.list !== 'function') {
    return refusDeCapacite(pre, 'blocks.list',
      "Sans relecture de la story, une écriture d'en-tête serait invérifiable — et un en-tête invérifiable dans un "
      + 'acte déposé est pire qu\'un en-tête non écrit.');
  }

  // Chaque paragraphe demandé, son texte séparé des numéros de page qu'il demande (`{page}`, `{pages}`).
  const paragraphes = (input.paragraphs ?? []).map((paragraphe) => ({ ...paragraphe, ...lireLesJetons(paragraphe.text) }));
  const poseDesNumeros = paragraphes.some((paragraphe) => paragraphe.champs.length > 0);
  const insererUnChamp = operationDuDocument(ctx.doc, 'fields', 'insert');
  if (poseDesNumeros && insererUnChamp === undefined) {
    return refusDeCapacite(pre, 'fields.insert',
      "Elle n'est exigée que pour un numéro de page ({page}, {pages}). Écris le texte sans numéro si l'avocat le veut "
      + 'quand même, et dis-lui que ce document ne permet pas de numéroter ses pages depuis Hector.');
  }

  const sections = entrees(await listerLesSections({}));
  const entreeDeSection = sections[input.section_index];
  const section = entreeDeSection === undefined ? null : adresseDeSection(entreeDeSection);
  if (section === null) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'SELECTOR_NOT_FOUND',
      message: `set_header_footer a été REFUSÉ avant toute écriture : ce document ne porte pas de section de rang `
        + `${input.section_index}. Il en compte ${sections.length} (rangs 0 à ${Math.max(0, sections.length - 1)}). `
        + 'Rien n\'a été modifié. Relis les sections par canvas_inspect avec includeDomains contenant '
        + '"headersFooters" et reprends la valeur "section_index" telle quelle ; la plupart des actes n\'ont qu\'une '
        + 'section, donc section_index vaut 0.',
      nextAction: 'Reprends un section_index publié par canvas_inspect, ou omets-le pour viser la première section.',
      recovery: { action: 'reinspect', reason: 'Rien n\'a été écrit ; la liste relue des sections dira quels rangs existent.' },
    });
  }

  const slot = slotDEnTete(section, input.target, input.type);
  const story = storyDuSlot(section, input.target, input.type);
  const resolutionAvant = lireLaResolution(await resoudreLeSlot({ target: slot }));
  const statutAvant = resolutionAvant.statut;

  // ── L'état AVANT, relu pendant qu'il est encore vrai ──────────────────────
  // ⚠️ Aucune lecture de blocs sur un slot `none` : le locator de story écrit en
  // `materializeIfInherited` par défaut, et faire naître une partie pour
  // répondre à une question serait une lecture qui écrit.
  const blocsAvant = statutAvant === 'none' ? [] : blocsDe(await ctx.doc.blocks.list({ in: story, includeText: true, offset: 0, limit: CANVAS_HEADER_FOOTER_BLOCKS_MAX }));
  const porteursDImageAvant =
    statutAvant === 'none'
      ? { porteurs: new Set<string>(), fiable: true }
      : await blocsPorteursDImage(ctx, story);
  const texteAvant = blocsAvant
    .map((bloc) => (chaine(bloc.text) ?? chaine(bloc.textPreview) ?? '').trim())
    .filter((texte) => texte.length > 0)
    .join('\n');
  const avaitUneImage = porteursDImageAvant.porteurs.size > 0;

  const warnings: string[] = [];

  // ── La page 1 est-elle détachée de la zone qu'on s'apprête à écrire ? ─────
  // ⚠️ Retour du 2026-09-06 : sur un acte dont la page de garde portait le
  // papier à en-tête — donc `titlePage` levé par la pose —, un pied de page
  // écrit en `primary` n'apparaissait pas page 1, et l'agent a pourtant annoncé
  // « en pied de page de toutes les pages ». La variante de première page est
  // une zone DISTINCTE : `primary` ne la couvre pas.
  // `titlePage` est omis tant qu'il n'a jamais été levé (cf. `booleenDeSection`
  // du snapshot) : seul un `true` explicite déclenche l'avertissement, une clé
  // absente ne prouve rien.
  if (input.type === 'primary' && entreeDeSection?.titlePage === true && input.remove_page_number !== true) {
    warnings.push(`Ce document a une PREMIÈRE PAGE DISTINCTE : elle porte sa propre zone `
      + `${NOM_DE_CIBLE[input.target]}, que cette écriture n'a pas touchée. Ce que tu viens d'écrire `
      + `apparaît à partir de la page 2. Pour que la page 1 le porte aussi, refais le même appel avec `
      + `type:"first_page" — sauf si la page de garde doit rester distincte, ce qui est le cas quand `
      + `elle porte le papier à en-tête du cabinet. N'annonce jamais « toutes les pages » ici.`);
  }

  // ── L'autre partie, lue AVANT le réglage qui peut la changer ──────────────
  const autrePartie = input.target === 'header' ? 'footer' : 'header';
  const autreAvant: AutrePartieAvant | null = input.type === 'primary'
    ? null
    : {
        partie: autrePartie,
        primaire: await lireUnePartie(ctx, resoudreLeSlot, section, autrePartie, 'primary'),
        zone: await lireUnePartie(ctx, resoudreLeSlot, section, autrePartie, input.type),
      };
  let sortDeLAutrePartie: SortDeLAutrePartie = { part: autrePartie, outcome: 'untouched' };

  // ── Les drapeaux de section, POSÉS avant d'écrire ─────────────────────────
  // Même ordre que `canvasLetterhead` : c'est le drapeau qui fait EXISTER la
  // variante. Levé après l'écriture, un échec laisserait un texte écrit dans
  // une zone que Word ne peint pas — l'agent annoncerait un en-tête invisible.
  const attendueInitiale = await revisionCourante(ctx, ctx.expectedRevision ?? pre.revision);
  /**
   * La révision à présenter au verrou optimiste du PROCHAIN appel mutant.
   *
   * ⚠️ **Elle se relit après chaque mutation, et l'oublier est un refus, pas un
   * défaut silencieux.** Vu rouge le 2026-09-05 sur le vrai SuperDoc :
   * `sections.setTitlePage` fait passer la révision de 0 à 2, et le
   * `create.paragraph` qui suivait présentait encore la révision de
   * l'inspection — `REVISION_MISMATCH — expected-revision 0 does not match
   * current revision 2`, donc un en-tête de première page jamais écrit alors
   * que le drapeau, lui, avait été levé. Même mécanique que le chaînage
   * d'`insert_paragraphs` (#990), mais elle commence ici DÈS le premier
   * paragraphe, parce que les drapeaux de section mutent avant lui.
   *
   * `ctx.expectedRevision` ne vaut donc que pour la PREMIÈRE mutation de
   * l'action, quelle qu'elle soit : c'est le verrou contre une frappe de
   * l'avocat depuis l'inspection, périmé par notre propre écriture.
   */
  let premiereMutationFaite = false;
  const prochaineRevision = async (): Promise<Revision> => {
    if (!premiereMutationFaite) {
      premiereMutationFaite = true;
      return attendueInitiale;
    }
    return revisionCourante(ctx, attendueInitiale);
  };
  /**
   * Les écritures que l'éditeur a ACCEPTÉES : une entrée d'historique chacune (relevé du 2026-10-05,
   * `mesure-annulation-entete-et-pied.spec.ts`), donc le compte qu'`undo_changes` doit défaire. Un `NO_OP` (réglage
   * déjà en place) et un refus n'en laissent pas. Toute écriture de ce verbe passe par `ecrire` : c'est là qu'elle
   * se compte, y compris la copie de l'autre partie.
   */
  let ecrituresFaites = 0;
  const ecrire: Ecrire = async (operation, appel) => {
    const options = { changeMode: 'direct' as const, expectedRevision: await prochaineRevision() };
    const recu = await muterSousFences(ctx, `set_header_footer:${operation}`, options, () => appel(options));
    if (receiptEstEnSucces(recu)) ecrituresFaites += 1;
    return recu;
  };
  /** Après un réglage de section LEVÉ par cet appel : garde à la partie non demandée ce qu'elle montrait. */
  const garderLAutre = async (): Promise<SortDeLAutrePartie> => (autreAvant === null
    ? sortDeLAutrePartie
    : garderLAutrePartie(ctx, { section, zone: input.type, avant: autreAvant, resoudreLeSlot, ecrire }));
  let drapeauDePremierePage: CanvasHeaderFooterReceipt['first_page_flag'] = 'not_applicable';

  // ── Ce que cet appel a déjà changé : ce qu'un refus en cours de route laisse en place ──
  let pagesPairesActivees = false;
  let partieCreee = false;
  let partieRattachee = false;
  const nodeIdsEcrits = new Set<string>();
  const blocsEcrits: Array<{ nodeId: string; nodeType: string; alignment?: 'left' | 'center' | 'right'; attendus: CanvasPageNumberField[] }> = [];
  /** Champs de numéro de page posés ou retirés par cet appel, et paragraphes retirés avec leur numéro. */
  let champsPoses = 0;
  let champsRetires = 0;
  /** Les champs qui ne sont pas des numéros de page (une date, un renvoi), partis avec le bloc qui les portait : jamais dits « numéro de page ». */
  let autresChampsRetires = 0;
  const paragraphesRetires: string[] = [];
  /** Ce que le fichier relu dit des numéros de page de la zone ; reste `undefined` pour un appel qui n'en parle pas. */
  let numerosDePage: CanvasHeaderFooterPageNumbers | undefined;

  /**
   * La zone visée telle que le fichier exporté la porte, ou la cause pour laquelle elle n'est pas lue : le message de
   * l'export ou du lecteur, ou la partie que le fichier ne porte pas, nommée.
   * Retrouvée par l'identifiant de relation que rend `headerFooters.resolve` : celui du fichier (`copieDePartie.ts`).
   */
  const relireLaZoneAuFichier = async (): Promise<ZoneRelueAuFichier> => {
    let refId: string | undefined;
    try {
      ({ refId } = lireLaResolution(await resoudreLeSlot({ target: slot })));
    } catch (erreur) {
      return { cause: `headerFooters.resolve a levé : ${erreur instanceof Error ? erreur.message : String(erreur)}` };
    }
    if (refId === undefined) return { cause: "headerFooters.resolve ne rend pas d'identifiant de relation pour cette zone" };
    const relu = await relireLeFichierDeLActe(ctx.doc);
    if ('cause' in relu) return { cause: relu.cause };
    const zone = relu.acte.horsDuCorps.find((lieu) => lieu.partie.genre === input.target && lieu.partie.refId === refId);
    return zone === undefined
      ? { cause: `le fichier exporté ne porte pas de partie « ${input.target} » sous l'identifiant de relation ${refId}` }
      : { zone };
  };
  const paragraphesDe = (relue: ZoneRelueAuFichier): ParagrapheRelu[] | null => ('zone' in relue ? [...relue.zone.paragraphes, ...relue.zone.paragraphesDeTableau] : null);

  const listerLesChamps = operationDuDocument(ctx.doc, 'fields', 'list');
  const retirerUnChamp = operationDuDocument(ctx.doc, 'fields', 'remove');
  /** Les champs que l'éditeur publie pour un bloc de CETTE partie (`storyId` : genre, puis chemin de la partie dans l'archive). */
  const champsDuBloc = async (nodeId: string, storyId: string): Promise<Array<{ adresse: Record<string, unknown>; instruction: string }>> => (
    listerLesChamps === undefined ? [] : entrees(await listerLesChamps({})).flatMap((entree) => {
      const adresse = isRecord(entree.address) ? entree.address : undefined;
      return adresse !== undefined && chaine(adresse.blockId) === nodeId && chaine(adresse.storyId) === storyId
        ? [{ adresse, instruction: chaine(entree.instruction) ?? chaine(entree.fieldType) ?? '' }]
        : [];
    })
  );
  /** `storyId` sous lequel l'éditeur range les champs d'une partie : son genre, puis son chemin dans l'archive (mesuré : `footer:/word/footer1.xml`). */
  const storyIdDeLaPartie = (chemin: string | undefined): string | undefined => (chemin === undefined ? undefined : `${input.target}:/${chemin}`);
  /** Le `storyId` de la zone visée, lu au fichier une fois par appel, à la première suppression qui en a besoin : la partie ne change plus après. */
  let storyIdDeLaZoneLu: { storyId: string | undefined } | undefined;
  /**
   * Le `storyId` sous lequel l'éditeur range les champs d'un bloc de la zone visée ; `undefined` : ce bloc n'y porte
   * aucun champ, ou la partie n'a pas été retrouvée au fichier.
   *
   * ⚠️ Un identifiant de paragraphe ne désigne pas une partie : la copie d'un pied pour la première page garde ceux
   * de sa source. Un `storyId` publié pour ce bloc n'est donc retenu que s'il est celui de la zone VISÉE, lu au
   * fichier — même quand il est le seul. Le prendre sans le vérifier retirait les champs du pied des autres pages en
   * réécrivant celui de la première (vu rouge le 2026-10-07, `canvas-numero-de-page.spec.ts`, « deux pieds au même
   * identifiant »).
   */
  const storyIdDesChampsDuBloc = async (nodeId: string): Promise<string | undefined> => {
    if (listerLesChamps === undefined) return undefined;
    const publies = new Set(entrees(await listerLesChamps({})).flatMap((entree) => {
      const adresse = isRecord(entree.address) ? entree.address : undefined;
      const storyId = chaine(adresse?.storyId);
      return chaine(adresse?.blockId) === nodeId && storyId !== undefined && storyId.startsWith(`${input.target}:`) ? [storyId] : [];
    }));
    // Aucun champ sous cet identifiant : rien à retirer, et pas d'export pour le savoir.
    if (publies.size === 0) return undefined;
    if (storyIdDeLaZoneLu === undefined) {
      const relue = await relireLaZoneAuFichier();
      storyIdDeLaZoneLu = { storyId: storyIdDeLaPartie('zone' in relue ? relue.zone.chemin : undefined) };
    }
    const { storyId } = storyIdDeLaZoneLu;
    return storyId !== undefined && publies.has(storyId) ? storyId : undefined;
  };
  /**
   * Retire les champs d'un bloc, un par un ; rend le refus de l'éditeur, ou `null`. La liste est relue avant
   * chaque retrait : le rang d'un champ se décale quand celui qui le précède part (mesuré). Bornée à
   * `CANVAS_PARAGRAPH_FIELDS_MAX` retraits par bloc.
   */
  const retirerLesChampsDuBloc = async (nodeId: string, storyId: string, seulementLesNumeros: boolean): Promise<{ code: string; message: string } | null> => {
    if (retirerUnChamp === undefined) return null;
    const aRetirer = async () => (await champsDuBloc(nodeId, storyId)).find(({ instruction }) => !seulementLesNumeros || numeroDePageDe(instruction) !== undefined);
    for (let retraits = 0; ; retraits += 1) {
      const champ = await aRetirer();
      if (champ === undefined) return null;
      // La borne atteinte avec un champ encore là : un refus nommé — sortir en succès annoncerait un numéro retiré qui reste.
      if (retraits === CANVAS_PARAGRAPH_FIELDS_MAX) {
        return {
          code: 'HEADER_FOOTER_TOO_MANY_FIELDS',
          message: `ce paragraphe porte plus de champs que la borne d'un appel : ${CANVAS_PARAGRAPH_FIELDS_MAX} champ(s) en ont été retirés, il en reste. `
            + "C'est la borne du verbe, pas un refus de l'éditeur : relis la zone, puis retire le reste par un nouvel appel remove_page_number",
        };
      }
      const recu = await ecrire('fields.remove', (options) => retirerUnChamp({ target: champ.adresse, mode: 'raw' }, options));
      if (!receiptEstEnSucces(recu)) return refusDuFournisseur(recu);
      if (numeroDePageDe(champ.instruction) === undefined) autresChampsRetires += 1;
      else champsRetires += 1;
    }
  };
  /**
   * Supprime un bloc de la zone. Ses champs partent d'abord : l'éditeur JETTE (`paragraph-tracked-wrapper-unsupported`)
   * sur la suppression d'un paragraphe qui porte encore les champs d'un pied de pagination (mesuré le 2026-10-07,
   * `canvas-numero-de-page.spec.ts` : un `replace` sur le pied du gabarit, sous suivi activé), et le supprime une
   * fois ses champs retirés. Une exception de l'éditeur sur cet appel est rendue comme un refus : le texte demandé
   * est déjà écrit, elle ne doit pas faire perdre le reçu.
   *
   * @returns `null` : le bloc est supprimé. Sinon le refus, et le nombre de champs que ce bloc a DÉJÀ perdus — un
   *   bloc resté en place sans ses champs n'est plus celui d'avant, et l'avertissement doit le dire.
   */
  const supprimerUnBlocDeLaZone = async (
    supprimer: MethodeProvider,
    cible: NonNullable<ReturnType<typeof adresseDeBloc>>,
    storyIdConnu?: string,
  ): Promise<EchecDeSuppression | null> => {
    const storyId = storyIdConnu ?? await storyIdDesChampsDuBloc(cible.nodeId);
    const retiresAvant = champsRetires + autresChampsRetires;
    const refusDeChamp = storyId === undefined ? null : await retirerLesChampsDuBloc(cible.nodeId, storyId, false);
    const champsDejaRetires = champsRetires + autresChampsRetires - retiresAvant;
    if (refusDeChamp !== null) return { ...refusDeChamp, etape: 'le retrait de ses champs', champsDejaRetires };
    const recu = await ecrire('blocks.delete', async (options) => {
      try {
        return await supprimer({ target: cible }, options);
      } catch (erreur) {
        return { success: false, failure: { code: 'EDITOR_THREW', message: erreur instanceof Error ? erreur.message : String(erreur) } };
      }
    });
    const echec = codeDEchec(recu);
    return echec === null || echec === 'NO_OP' ? null : { ...refusDuFournisseur(recu), code: echec, etape: 'sa suppression', champsDejaRetires };
  };

  /** Les faits du reçu, RELUS à l'instant de l'appel : après l'écriture, ou après le refus qui l'a interrompue. */
  const lireLesFaits = async (): Promise<CanvasHeaderFooterReceipt> => {
    const partieRelue = async (cible: PartieDEnTete, zone: 'first_page' | 'primary') => {
      const lue = await lireUnePartie(ctx, resoudreLeSlot, section, cible, zone);
      return { status: lue.statut, text: lue.texte };
    };
    return {
      target: input.target,
      type: input.type,
      section_index: input.section_index,
      mode: input.mode,
      paragraphs_written: blocsEcrits.length,
      previous_text: extrait(texteAvant),
      previous_had_image: avaitUneImage,
      first_page_flag: drapeauDePremierePage,
      warnings,
      writes: ecrituresFaites,
      ...(numerosDePage === undefined ? {} : { page_numbers: numerosDePage }),
      parts_after: {
        first_page_distinct: premierePageDistincteRelue(await listerLesSections({}), input.section_index),
        header: { first_page: await partieRelue('header', 'first_page'), other_pages: await partieRelue('header', 'primary') },
        footer: { first_page: await partieRelue('footer', 'first_page'), other_pages: await partieRelue('footer', 'primary') },
      },
      other_part: sortAuContrat(sortDeLAutrePartie),
    };
  };

  /**
   * Le reçu d'un refus de l'éditeur en cours de route.
   *
   * Rien n'a encore changé : `aborted`, le document est intact. Sinon `partial` : ce que l'appel a déjà fait
   * (réglage de section levé, autre partie copiée, partie créée, paragraphes écrits) reste dans le document,
   * rien n'est défait, et le reçu le dit avec les parties relues. Dire « refusé avant toute écriture » d'un
   * appel qui a détaché la première page ferait annoncer à l'avocat un acte inchangé.
   */
  const refuser = async (
    etape: string,
    refus: { code: string; message: string },
    siRienNaChange: { nextAction: string; recovery?: CanvasActionReceipt['recovery'] },
  ): Promise<CanvasActionReceipt> => {
    const zoneVisee = `${NOM_DE_CIBLE[input.target]} ${NOM_DE_ZONE[input.type]} de la section ${input.section_index}`;
    const changements = [
      ...(drapeauDePremierePage === 'raised' ? [`la première page distincte a été activée sur la section ${input.section_index}`] : []),
      ...(pagesPairesActivees ? [`la distinction pages paires / impaires a été activée sur la section ${input.section_index}`] : []),
      ...(sortDeLAutrePartie.outcome === 'kept_by_copy'
        ? [`${NOM_DE_CIBLE[autrePartie]} ${NOM_DE_ZONE[input.type]} a reçu une copie de celui des autres pages`]
        : []),
      ...(sortDeLAutrePartie.outcome === 'copy_unverified'
        ? [`${NOM_DE_CIBLE[autrePartie]} ${NOM_DE_ZONE[input.type]} a reçu une copie de celui des autres pages, non vérifiée `
          + `(headerFooter.other_part, raison « ${sortDeLAutrePartie.reason} »)`]
        : []),
      ...(sortDeLAutrePartie.outcome === 'changed'
        ? [`${NOM_DE_CIBLE[autrePartie]} a changé sur ${PAGES_DE_ZONE[input.type]} (headerFooter.other_part, raison « ${sortDeLAutrePartie.reason} »)`]
        : []),
      ...(partieRattachee ? [`${NOM_DE_CIBLE[input.target]} ${NOM_DE_ZONE[input.type]} a été créé, avec un paragraphe vide`] : []),
      ...(partieCreee && !partieRattachee ? [`une partie a été créée pour ${zoneVisee} sans être rattachée : elle ne peint rien`] : []),
      ...(blocsEcrits.length > 0 ? [`${blocsEcrits.length} paragraphe(s) y ont été écrits`] : []),
      ...(champsPoses > 0 ? [`${champsPoses} champ(s) de numéro de page y ont été posés`] : []),
      ...(champsRetires > 0 ? [`${champsRetires} champ(s) de numéro de page en ont été retirés`] : []),
      ...(autresChampsRetires > 0 ? [`${autresChampsRetires} autre(s) champ(s) en ont été retirés`] : []),
      ...(paragraphesRetires.length > 0 ? [`${paragraphesRetires.length} paragraphe(s) qui portaient un numéro de page en ont été retirés`] : []),
    ];
    if (changements.length === 0) {
      return abortedBeforeMutation({
        intent: input.action,
        snapshot: toCanvasSnapshotSummary(pre),
        code: refus.code,
        message: `set_header_footer a été REFUSÉ avant toute écriture : l'éditeur a refusé ${etape} sur ${zoneVisee}. `
          + `Refus rendu tel quel : « ${refus.message} ». Aucun paragraphe n'a été écrit, rien n'a été modifié.`,
        nextAction: siRienNaChange.nextAction,
        ...(siRienNaChange.recovery === undefined ? {} : { recovery: siRienNaChange.recovery }),
      });
    }
    const post = await snapshot(ctx);
    ctx.verifierEpoque('set_header_footer:refus');
    return {
      ...partialAfterMutation({
        intent: input.action,
        preSnapshot: toCanvasSnapshotSummary(pre),
        postSnapshot: toCanvasSnapshotSummary(post),
        resolvedTargets: [],
        code: refus.code,
        message: `set_header_footer n'a pas abouti : l'éditeur a refusé ${etape} sur ${zoneVisee}. Refus rendu tel quel : `
          + `« ${refus.message} ». Ce que l'appel avait déjà changé RESTE dans le document, rien n'est défait : `
          + `${changements.join(' ; ')}. ${ceQueRendentLesPartiesRelues(input.type)} Ne rejoue pas l'appel entier. Dis à l'avocat ce `
          + 'qui a changé. Pour reprendre, relis la section par canvas_inspect avec includeDomains contenant '
          + `"headersFooters" et ne repose que ce qui manque. ${pourAnnuler(ecrituresFaites)}`,
        verification: [],
        effects: [{
          kind: 'set_header_footer',
          reviewability: 'direct_untracked',
          description: `Avant le refus de l'éditeur : ${changements.join(' ; ')}.`,
          nodeIds: [...nodeIdsEcrits],
        }],
        recoveryReason: "Une partie de l'appel est déjà dans le document : le rejouer entier ne déferait rien et dupliquerait ce qui est écrit.",
      }),
      headerFooter: await lireLesFaits(),
    };
  };

  // ── Retirer le numéro de page : aucun réglage de section, aucune partie créée ──
  if (input.remove_page_number === true) {
    const zoneVisee = `${NOM_DE_CIBLE[input.target]} ${NOM_DE_ZONE[input.type]} de la section ${input.section_index}`;
    const rienARetirer = (raison: string): CanvasActionReceipt => abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'PAGE_NUMBER_NOT_FOUND',
      message: `set_header_footer n'a rien retiré : ${raison} Rien n'a été modifié. Pour savoir quelle zone numérote, lis-la par `
        + `canvas_inspect avec formatOf:{kind:"all",in:{kind:"${input.target}",zone:"${input.type}"}} : un paragraphe qui porte un numéro `
        + 'de page le dit par pageNumbers. Une première page distincte et des pages paires ont chacune leur zone.',
      nextAction: "Vise la zone qui porte le numéro de page, ou dis à l'avocat que cette zone n'en porte pas.",
      recovery: { action: 'stop', reason: 'Cette zone ne porte pas de numéro de page : le même appel rendrait le même refus.' },
    });
    if (statutAvant === 'none') return rienARetirer(`${zoneVisee} ne porte rien.`);
    if (statutAvant === 'inherited') {
      return rienARetirer(`${zoneVisee} reprend la zone d'une section antérieure ; retire le numéro dans la section qui la porte (section_index plus petit).`);
    }
    if (listerLesChamps === undefined || retirerUnChamp === undefined) {
      return refusDeCapacite(pre, 'fields.list et fields.remove',
        "Elles ne sont exigées que pour retirer un numéro de page. Dis à l'avocat de le retirer dans Word, après téléchargement.");
    }
    const relueAvant = await relireLaZoneAuFichier();
    if ('cause' in relueAvant) {
      return abortedBeforeMutation({
        intent: input.action,
        snapshot: toCanvasSnapshotSummary(pre),
        code: 'HEADER_FOOTER_FILE_UNREADABLE',
        message: `set_header_footer n'a rien retiré : ${zoneVisee} n'a pas pu être lu dans le fichier de l'acte (cause rendue telle quelle : « ${relueAvant.cause} »). `
          + "C'est dans ce fichier que se lisent les numéros de page — l'éditeur ne les rend pas en texte. Rien n'a été modifié.",
        nextAction: "Rejoue le même appel une fois ; s'il est refusé de même, dis à l'avocat que le numéro de page n'a pas pu être retiré.",
        recovery: { action: 'retry', reason: "Rien n'a été écrit ; l'export du fichier peut aboutir au second essai." },
      });
    }
    const zoneAvant = relueAvant.zone;
    const porteurs = [...zoneAvant.paragraphes, ...zoneAvant.paragraphesDeTableau].filter((lu) => lu.numerosDePage.length > 0);
    if (porteurs.length === 0) return rienARetirer(`${zoneVisee} ne porte aucun numéro de page.`);

    const storyIdDeLaZone = storyIdDeLaPartie(zoneAvant.chemin);
    // Liée à sa famille : appelée nue, l'opération perd le receveur qu'elle lit et lève (revue de la PR #2257).
    const supprimerUnBloc = operationDuDocument(ctx.doc, 'blocks', 'delete');
    let paragraphesLaisses = 0;
    for (const porteur of porteurs) {
      const texte = extrait(porteur.passages.map((passage) => passage.texte).join(''));
      const { nodeId } = porteur;
      if (nodeId === null) {
        paragraphesLaisses += 1;
        warnings.push(`Un paragraphe qui porte un numéro de page (« ${texte} ») n'a pas d'identifiant dans le fichier : il n'a pas pu être `
          + "visé et reste en place. Dis-le à l'avocat.");
        continue;
      }
      // 1. Les champs de numéro de page du paragraphe.
      if (storyIdDeLaZone !== undefined) {
        const refus = await retirerLesChampsDuBloc(nodeId, storyIdDeLaZone, true);
        if (refus !== null) {
          return refuser('le retrait d\'un champ de numéro de page', refus, {
            nextAction: "Relis la zone par canvas_inspect avec formatOf et in, puis dis à l'avocat ce qui reste.",
          });
        }
      }
      // 2. Le paragraphe qui portait le numéro — « Page  / » sans ses champs ne dit plus rien. Règle dure du
      //    module : un paragraphe qui porte une image n'est jamais supprimé.
      const blocsCourants = blocsDe(await ctx.doc.blocks.list({ in: story, includeText: true, offset: 0, limit: CANVAS_HEADER_FOOTER_BLOCKS_MAX }));
      const bloc = blocsCourants.find((candidat) => chaine(candidat.nodeId) === nodeId);
      const cible = bloc === undefined ? null : adresseDeBloc(bloc, story);
      const resteVide = (chaine(bloc?.text) ?? chaine(bloc?.textPreview) ?? '').trim().length === 0;
      const peutPorterUneImage = porteursDImageAvant.porteurs.has(nodeId) || (resteVide && !porteursDImageAvant.fiable);
      if (cible === null || peutPorterUneImage || typeof supprimerUnBloc !== 'function') {
        paragraphesLaisses += 1;
        warnings.push(`Le numéro de page est retiré, mais le paragraphe qui le portait est RESTÉ`
          + (peutPorterUneImage ? ' : il porte une image, ou cet éditeur n\'a pas su dire s\'il en porte une' : '')
          + ` (« ${extrait(chaine(bloc?.text) ?? '')} »). Dis-le à l'avocat.`);
        continue;
      }
      // Le seul paragraphe de la partie : un paragraphe vide d'abord, sans quoi la partie resterait sans
      // paragraphe (mesuré : `<w:ftr></w:ftr>`). Écrire puis supprimer, comme partout dans ce module.
      if (blocsCourants.length === 1) {
        const vide = await ecrire('create.paragraph', (options) => ctx.doc.create?.paragraph?.({ in: story, at: { kind: 'documentEnd' } }, options));
        if (!receiptEstEnSucces(vide)) {
          return refuser('la création du paragraphe vide qui garde la zone valide', refusDuFournisseur(vide), {
            nextAction: "Relis la zone par canvas_inspect avec formatOf et in, puis dis à l'avocat ce qui reste.",
          });
        }
      }
      const echec = await supprimerUnBlocDeLaZone(supprimerUnBloc, cible, storyIdDeLaZone);
      if (echec !== null) {
        paragraphesLaisses += 1;
        warnings.push(`Le numéro de page est retiré, mais le paragraphe qui le portait n'a pas pu être supprimé (${direLEchecDeSuppression(echec)}) : `
          + `son texte reste (« ${extrait(chaine(bloc?.text) ?? '')} »). Dis-le à l'avocat.`);
      // Le texte cité est celui de la ligne une fois ses numéros retirés : ce que l'avocat perd avec elle. Le texte lu au
      // fichier porterait aussi le dernier résultat du champ (« page 1 »).
      } else paragraphesRetires.push(extrait(chaine(bloc?.text) ?? chaine(bloc?.textPreview) ?? ''));
    }

    const post = await snapshot(ctx);
    ctx.verifierEpoque('set_header_footer:receipt');
    const relueApres = await relireLaZoneAuFichier();
    const causeDeNonLecture = 'cause' in relueApres ? relueApres.cause : undefined;
    // La liste est bornée au contrat : au-delà, le reçu entier serait refusé au schéma. Le reste est compté, jamais tu.
    const nonCites = paragraphesRetires.length - CANVAS_REMOVED_PARAGRAPHS_MAX;
    numerosDePage = {
      requested: 'remove',
      ...jugerLeRetrait(paragraphesDe(relueApres)),
      removed_paragraphs: paragraphesRetires.slice(0, CANVAS_REMOVED_PARAGRAPHS_MAX),
      ...(nonCites > 0 ? { removed_paragraphs_not_listed: nonCites } : {}),
    };
    const statutApres = lireLeStatutDeSlot(await resoudreLeSlot({ target: slot }));
    const recu = buildVerifiedReceipt({
      intent: input.action,
      regimeSuivi: ctx.regimeSuivi,
      preSnapshot: toCanvasSnapshotSummary(pre),
      postSnapshot: toCanvasSnapshotSummary(post),
      resolvedTargets: [],
      provider: { success: true },
      revisionsAvant: idsDeRevision(pre.trackedChanges),
      revisionsApres: idsDeRevision(post.trackedChanges),
      mouvementAttendu: 'aucun',
      verification: [{
        name: 'zone_gardee',
        passed: statutApres === 'explicit',
        message: statutApres === 'explicit'
          ? `Relecture : ${zoneVisee} est toujours rattaché à sa section.`
          : `Après le retrait, ${zoneVisee} se relit « ${statutApres} » au lieu de « explicit ». Relis les zones par canvas_inspect avec includeDomains contenant "headersFooters" avant toute autre action.`,
      }],
      headerFooter: await lireLesFaits(),
      effects: [{
        kind: 'set_header_footer',
        reviewability: 'direct_untracked',
        description: `${effetDuRetrait({ zoneVisee, champsRetires, autresChampsRetires, paragraphesRetires, paragraphesLaisses })} ${pourAnnuler(ecrituresFaites)}`,
        nodeIds: [],
      }],
    });
    // Le verdict est celui du fichier : un paragraphe gardé sans son champ (il porte une image) n'est pas un numéro resté.
    return avecUnConstat(recu, controleDesNumerosDePage(numerosDePage, zoneVisee, causeDeNonLecture), numerosDePage.outcome === 'conforming' ? undefined : {
      code: numerosDePage.outcome === 'unreadable'
        ? 'HEADER_FOOTER_PAGE_NUMBER_UNVERIFIED'
        : (numerosDePage.fields_after ?? []).length > 0 ? 'HEADER_FOOTER_PAGE_NUMBER_NOT_REMOVED' : 'HEADER_FOOTER_ZONE_WITHOUT_PARAGRAPH',
      message: messageDesNumerosNonTenus(numerosDePage, zoneVisee, causeDeNonLecture),
    });
  }

  if (input.type === 'first_page') {
    const poser = operationDuDocument(ctx.doc, 'sections', 'setTitlePage');
    if (poser === undefined) {
      return refusDeCapacite(pre, 'sections.setTitlePage',
        "Elle n'est exigée que pour type:\"first_page\" : sans elle, Word ne peint jamais la variante de première "
        + 'page et le texte écrit resterait invisible. Utilise type:"primary" pour poser la mention sur toutes les '
        + 'pages, ce que l\'avocat préférera à un en-tête qui ne s\'affiche pas.');
    }
    const recu = await ecrire('sections.setTitlePage', (options) => poser({ target: section, enabled: true }, options));
    const echec = codeDEchec(recu);
    // `NO_OP` = le drapeau était DÉJÀ levé. C'est un succès, et le distinguer
    // est ce qui permet au receipt de dire « already_on » plutôt que « raised »
    // — l'agent ne doit pas annoncer à l'avocat un réglage qu'il n'a pas changé.
    if (echec === null) {
      drapeauDePremierePage = 'raised';
      sortDeLAutrePartie = await garderLAutre();
    } else if (echec === 'NO_OP') drapeauDePremierePage = 'already_on';
    else {
      return abortedBeforeMutation({
        intent: input.action,
        snapshot: toCanvasSnapshotSummary(pre),
        code: echec,
        message: `set_header_footer a été REFUSÉ avant toute écriture : l'éditeur n'a pas pu activer la première `
          + `page distincte sur la section ${input.section_index} (code ${echec}). Rien n'a été modifié. Sans ce `
          + 'réglage, un en-tête de première page n\'est peint nulle part.',
        nextAction: 'Repose la même mention avec type:"primary" — elle apparaîtra sur toutes les pages, ce qui est '
          + 'visible, là où type:"first_page" resterait invisible.',
        recovery: { action: 'retry', reason: 'Rien n\'a été écrit. Le même appel en type:"primary" n\'a pas besoin de ce réglage.' },
      });
    }
  }

  if (input.type === 'even_pages') {
    // Pendant exact du drapeau de première page, pour la variante paire :
    // `w:evenAndOddHeaders`. Le receipt n'a pas de champ dédié — son
    // `first_page_flag` est nommé pour le drapeau que le contrat fixe — donc
    // l'information passe par un warning explicite plutôt que par rien.
    const poser = operationDuDocument(ctx.doc, 'sections', 'setOddEvenHeadersFooters');
    if (poser === undefined) {
      return refusDeCapacite(pre, 'sections.setOddEvenHeadersFooters',
        "Elle n'est exigée que pour type:\"even_pages\" : sans elle, Word ne distingue pas les pages paires et le "
        + 'texte écrit resterait invisible. Utilise type:"primary" pour poser la mention sur toutes les pages.');
    }
    const recu = await ecrire('sections.setOddEvenHeadersFooters', (options) => poser({ target: section, enabled: true }, options));
    const echec = codeDEchec(recu);
    if (echec === null) {
      pagesPairesActivees = true;
      warnings.push('La distinction pages paires / pages impaires a été activée sur cette section : sans elle, un '
        + "en-tête de pages paires n'est peint nulle part. Les pages impaires gardent la zone \"primary\".");
      sortDeLAutrePartie = await garderLAutre();
    } else if (echec !== 'NO_OP') {
      return abortedBeforeMutation({
        intent: input.action,
        snapshot: toCanvasSnapshotSummary(pre),
        code: echec,
        message: `set_header_footer a été REFUSÉ avant toute écriture : l'éditeur n'a pas pu activer la distinction `
          + `pages paires / impaires sur la section ${input.section_index} (code ${echec}). Rien n'a été modifié.`,
        nextAction: 'Repose la même mention avec type:"primary".',
        recovery: { action: 'retry', reason: 'Rien n\'a été écrit. Le même appel en type:"primary" n\'a pas besoin de ce réglage.' },
      });
    }
  }

  // ── La MATÉRIALISATION, faite explicitement quand le slot n'a pas sa partie ─
  //
  // ⚠️ **Elle ne peut PAS être laissée à la première écriture, et c'est mesuré**
  // (2026-09-05, sonde `canvas-entetes-et-pieds`) : le locator de slot
  // matérialise en `materializeIfInherited`, ce qui **avance la révision**, et
  // le verrou optimiste est contrôlé APRÈS. Un `create.paragraph` présentant la
  // révision qu'il vient de lire est donc refusé par construction —
  // `REVISION_MISMATCH — expected-revision 0 does not match current revision 1` —
  // et la matérialisation, elle, a bien eu lieu. Le premier en-tête d'un
  // document n'était jamais écrit, sur un refus qui accuse un conflit
  // inexistant.
  //
  // La faire à découvert restaure un verrou qui veut dire quelque chose : chaque
  // appel présente la révision relue juste avant lui (`prochaineRevision`).
  // `sourceRefId` est passé sur un slot HÉRITÉ, sans quoi la copie locale
  // naîtrait vide et l'avocat perdrait l'en-tête que la section précédente lui
  // donnait.
  if (statutAvant !== 'explicit') {
    // ⚠️ Chemin à DEUX niveaux (`headerFooters.parts.create`) : chaque niveau
    // peut être un proxy appelable, donc chacun passe par `familleDuDocument`.
    const enTetes = familleDuDocument(ctx.doc, 'headerFooters');
    const creerLaPartie = operationDuDocument(enTetes, 'parts', 'create');
    const poserLaReference = operationDuDocument(enTetes, 'refs', 'set');
    if (creerLaPartie === undefined || poserLaReference === undefined) {
      return refusDeCapacite(pre, 'headerFooters.parts.create et headerFooters.refs.set',
        `Elles ne sont exigées que lorsque la zone visée n'a pas encore sa propre partie — c'est le cas ici `
        + `(${NOM_DE_CIBLE[input.target]} ${NOM_DE_ZONE[input.type]} de la section ${input.section_index} est `
        + `« ${statutAvant} »). Sur une zone que le document porte déjà, ce verbe fonctionne. Dis à l'avocat que ce `
        + 'document ne permet pas de créer un en-tête là où il n\'y en a pas.');
    }
    const partie = await ecrire(
      'headerFooters.parts.create',
      () => creerLaPartie(
        {
          kind: input.target,
          ...(statutAvant === 'inherited' && resolutionAvant.refId !== undefined
            ? { sourceRefId: resolutionAvant.refId }
            : {}),
        },
        { changeMode: 'direct' },
      ),
    );
    const refIdNeuf = isRecord(partie) ? chaine(partie.refId) : undefined;
    if (!receiptEstEnSucces(partie) || refIdNeuf === undefined) {
      return refuser('la création de la partie', refusDuFournisseur(partie), {
        nextAction: 'Relis la zone par canvas_inspect avec includeDomains contenant "headersFooters" : elle dira si '
          + 'une autre zone de cette section est déjà utilisable.',
      });
    }
    partieCreee = true;
    const rattachement = await ecrire(
      'headerFooters.refs.set',
      () => poserLaReference({ target: slot, refId: refIdNeuf }, { changeMode: 'direct' }),
    );
    if (!receiptEstEnSucces(rattachement)) {
      return refuser('le rattachement de la partie créée à la section', refusDuFournisseur(rattachement), {
        nextAction: 'Relis la zone par canvas_inspect avec includeDomains contenant "headersFooters" avant de retenter.',
      });
    }
    partieRattachee = true;
  }

  // ── L'écriture : un appel par paragraphe, chaînés ─────────────────────────
  // ⚠️ La révision attendue est RELUE avant chaque appel après le premier :
  // chaque création avance `doc.info().revision`, donc rejouer celle de la
  // pré-lecture ferait refuser le deuxième paragraphe en `REVISION_MISMATCH`
  // (même mécanique qu'`insert_paragraphs`, #990). `ctx.expectedRevision` ne
  // vaut que pour le PREMIER appel : c'est le verrou contre une frappe de
  // l'avocat depuis l'inspection, périmé par notre propre écriture.
  for (const paragraphe of paragraphes) {
    const recu = await ecrire('create.paragraph', (options) => ctx.doc.create?.paragraph?.(
      // `in` porte la story, `at` reste le vocabulaire d'emplacement DANS
      // cette story : `documentEnd` y désigne la fin de l'en-tête, pas la fin
      // de l'acte (mesuré). Le texte est écrit SANS ses jetons de numéro de page ;
      // un numéro seul (« {page} ») ne laisse aucun texte à écrire.
      { in: story, at: { kind: 'documentEnd' }, ...(paragraphe.texte === '' ? {} : { text: paragraphe.texte }) },
      options,
    ));
    if (!receiptEstEnSucces(recu)) {
      return refuser(`l'écriture du paragraphe ${blocsEcrits.length + 1}`, refusDuFournisseur(recu), {
        nextAction: 'Relis la zone par canvas_inspect avec includeDomains contenant "headersFooters", puis retente avec un '
          + 'texte plus court ou une autre zone.',
        recovery: { action: 'retry', reason: 'Rien n\'a été écrit dans la zone ; le document est intact.' },
      });
    }
    const cree = isRecord(recu) && isRecord(recu.paragraph) ? recu.paragraph : undefined;
    const nodeId = chaine(cree?.nodeId);
    if (nodeId !== undefined) {
      nodeIdsEcrits.add(nodeId);
      blocsEcrits.push({
        nodeId,
        nodeType: chaine(cree?.nodeType) ?? 'paragraph',
        ...(paragraphe.alignment === undefined ? {} : { alignment: paragraphe.alignment }),
        attendus: paragraphe.champs.map((champ) => champ.numero),
      });
    }
    if (paragraphe.champs.length === 0 || insererUnChamp === undefined) continue;
    if (nodeId === undefined) {
      return refuser(`la pose du numéro de page du paragraphe ${blocsEcrits.length + 1}`, {
        code: 'PARAGRAPH_WITHOUT_ID',
        message: "l'éditeur a écrit le paragraphe sans rendre son identifiant : le numéro de page n'a nulle part où se poser",
      }, { nextAction: 'Relis la zone par canvas_inspect avec includeDomains contenant "headersFooters".' });
    }
    // Du plus loin au plus près, à des positions comptées sur le texte ÉCRIT (`numeroDePage.ts`, fait mesuré
    // n° 2). L'adresse porte la story que l'éditeur a rendue pour ce paragraphe : sans elle, il cherche dans le corps.
    const storyDuParagraphe = isRecord(cree?.story) ? cree.story : story;
    for (const champ of ordreDePose(paragraphe.champs)) {
      const pose = await ecrire('fields.insert', (options) => insererUnChamp(
        {
          at: { kind: 'text', segments: [{ blockId: nodeId, range: { start: champ.position, end: champ.position } }], story: storyDuParagraphe },
          instruction: INSTRUCTION_DU_NUMERO[champ.numero],
          mode: 'raw',
        },
        options,
      ));
      if (!receiptEstEnSucces(pose)) {
        return refuser(`la pose du champ ${INSTRUCTION_DU_NUMERO[champ.numero]} dans le paragraphe ${blocsEcrits.length}`, refusDuFournisseur(pose), {
          nextAction: 'Relis la zone par canvas_inspect avec formatOf et in : le paragraphe est écrit, sans tous ses numéros. '
            + "Dis à l'avocat ce qui manque.",
        });
      }
      champsPoses += 1;
    }
  }

  // ── L'alignement : une finition, jamais une condition ─────────────────────
  // Un en-tête posé mais aligné à gauche reste très au-dessus d'un en-tête
  // absent — même arbitrage que `canvasLetterhead.finirLaMiseEnPage`. Le refus
  // se DIT (dans `warnings`), il ne fait pas échouer l'écriture.
  // Liée à `format.paragraph`, sa famille : détachée, l'opération lève sans son receveur.
  const alignerUnBloc = operationDuDocument(familleDuDocument(ctx.doc, 'format'), 'paragraph', 'setAlignment');
  for (const bloc of blocsEcrits) {
    if (bloc.alignment === undefined) continue;
    if (typeof alignerUnBloc !== 'function') {
      warnings.push("Cet éditeur ne sait pas régler l'alignement d'un paragraphe : le texte est en place mais aligné "
        + "au défaut du document. Ne l'annonce pas centré ou à droite à l'avocat.");
      break;
    }
    const recu = await ecrire('format.paragraph.setAlignment', (options) => alignerUnBloc(
      // ⚠️ La story fait PARTIE de l'adresse : sans elle, la cible vise le
      // corps du document et le fournisseur rend `TARGET_NOT_FOUND` (mesuré).
      { target: { kind: 'block', nodeType: bloc.nodeType, nodeId: bloc.nodeId, story }, alignment: bloc.alignment },
      options,
    ));
    const echec = codeDEchec(recu);
    if (echec !== null && echec !== 'NO_OP') {
      warnings.push(`L'alignement « ${bloc.alignment} » a été refusé sur un paragraphe (code ${echec}) : le texte est `
        + "en place, au défaut du document.");
    }
  }

  // ── Le ménage, APRÈS l'écriture et jamais avant ───────────────────────────
  const blocsApres = blocsDe(await ctx.doc.blocks.list({ in: story, includeText: true, offset: 0, limit: CANVAS_HEADER_FOOTER_BLOCKS_MAX }));
  const porteursDImageApres = await blocsPorteursDImage(ctx, story);
  const aSupprimer = blocsSupprimables({
    blocs: blocsApres,
    nodeIdsEcrits,
    porteursDImage: porteursDImageApres.porteurs,
    listeDImagesFiable: porteursDImageApres.fiable,
    mode: input.mode,
    partieNeuve: statutAvant !== 'explicit',
  });

  // Un `replace` qui laisse derrière lui un bloc sans texte alors qu'on n'a PAS
  // pu interroger les images : le dire, plutôt que d'annoncer un remplacement
  // net. Le cas visé est une ligne que la lecture rend vide sans qu'on sache si
  // elle porte une image — le papier à en-tête du cabinet, ou une ligne blanche.
  if (input.mode === 'replace' && !porteursDImageApres.fiable) {
    const restants = blocsApres.filter((bloc) => {
      const nodeId = chaine(bloc.nodeId);
      if (nodeId === undefined || nodeIdsEcrits.has(nodeId)) return false;
      return (chaine(bloc.text) ?? chaine(bloc.textPreview) ?? '').trim().length === 0;
    }).length;
    if (restants > 0) {
      warnings.push(`${restants} paragraphe(s) sans texte étaient déjà dans ${NOM_DE_CIBLE[input.target]} `
        + "et y sont RESTÉS : cet éditeur n'a pas su dire s'ils portaient une image, et ce verbe ne "
        + "risque jamais d'effacer le papier à en-tête du cabinet. Préviens l'avocat au lieu "
        + "d'annoncer un remplacement complet.");
    }
  }
  // Liée à sa famille, comme le retrait du numéro de page : détachée, l'opération lève sans son receveur.
  const supprimer = operationDuDocument(ctx.doc, 'blocks', 'delete');
  if (aSupprimer.length > 0 && typeof supprimer !== 'function') {
    warnings.push(`Cet éditeur ne sait pas supprimer un bloc : les ${aSupprimer.length} paragraphe(s) qui étaient `
      + `déjà dans ${NOM_DE_CIBLE[input.target]} y sont RESTÉS, au-dessus du texte que tu viens d'écrire. `
      + "Dis-le à l'avocat plutôt que d'annoncer un remplacement.");
  } else if (typeof supprimer === 'function') {
    for (const bloc of aSupprimer) {
      const cible = adresseDeBloc(bloc, story);
      if (cible === null) continue;
      const echec = await supprimerUnBlocDeLaZone(supprimer, cible);
      if (echec !== null) {
        warnings.push(`Un paragraphe préexistant n'a pas pu être retiré (${direLEchecDeSuppression(echec)}) : il reste dans `
          + `${NOM_DE_CIBLE[input.target]}, à côté de ce que tu viens d'écrire.`);
      }
    }
  }
  if (avaitUneImage) {
    warnings.push("Cette zone porte une IMAGE — le papier à en-tête du cabinet, le plus souvent. Elle a été "
      + "CONSERVÉE : ce verbe ne supprime jamais une image, même en mode \"replace\". Si l'avocat veut la retirer, "
      + 'il passe par le bouton « Appliquer l\'en-tête du cabinet » de la barre du canvas.');
  }

  const post = await snapshot(ctx);
  ctx.verifierEpoque('set_header_footer:receipt');

  // ── La postcondition : une RELECTURE, jamais l'accusé du fournisseur ──────
  const statutApres = lireLeStatutDeSlot(await resoudreLeSlot({ target: slot }));
  const relus = blocsDe(await ctx.doc.blocks.list({ in: story, includeText: true, offset: 0, limit: CANVAS_HEADER_FOOTER_BLOCKS_MAX }));
  // Les numéros de page ne se relisent qu'au fichier : un export, et seulement quand l'appel en a posé.
  let causeDeNonLecture: string | undefined;
  if (poseDesNumeros) {
    const relue = await relireLaZoneAuFichier();
    if ('cause' in relue) causeDeNonLecture = relue.cause;
    numerosDePage = { requested: 'set', ...jugerLaPose(blocsEcrits.map(({ nodeId, attendus }) => ({ nodeId, attendus })), paragraphesDe(relue)) };
  }
  const receiptDEnTete = await lireLesFaits();
  const premierePageRelue = receiptDEnTete.parts_after.first_page_distinct;

  const recu = buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider: { success: true },
    revisionsAvant: idsDeRevision(pre.trackedChanges),
    revisionsApres: idsDeRevision(post.trackedChanges),
    // ⚠️ MESURÉ : écrire dans une story d'en-tête ne pose AUCUNE révision
    // suivie, même sous fenêtre `suggesting`, alors que le témoin posé dans le
    // corps en pose une. Exiger une apparition ferait rendre `failed` à TOUTE
    // écriture réussie. Les identités restent relues : la troisième issue de
    // #939 — une révision disparue sans qu'on l'ait demandé — doit continuer de
    // s'exercer.
    mouvementAttendu: 'aucun',
    verification: [
      // Un paragraphe à numéro se relit avec le résultat de ses champs (« Page 1 / 3 ») : son texte n'est pas
      // comparé ici, ses champs le sont au fichier (`controleDesNumerosDePage`).
      controleDeZoneRelue(statutApres, relus, input, nodeIdsEcrits, paragraphes.filter((paragraphe) => paragraphe.champs.length === 0).map((paragraphe) => paragraphe.texte)),
      ...(input.type === 'first_page' ? [controleDePremierePageRelue(premierePageRelue, input.section_index)] : []),
    ],
    headerFooter: receiptDEnTete,
    effects: [{
      kind: 'set_header_footer',
      // Jamais `tracked` : mesuré, et le rabattement de `buildVerifiedReceipt`
      // ne va que dans le sens de la prudence — il ne rattraperait pas une
      // promesse trop forte.
      reviewability: 'direct_untracked',
      description: `${NOM_DE_CIBLE[input.target].replace(/^l'/, 'L\'').replace(/^le /, 'Le ')} `
        + `${NOM_DE_ZONE[input.type]} de la section ${input.section_index} porte désormais `
        + `${blocsEcrits.length} paragraphe(s)${champsPoses === 0 ? '' : `, dont ${champsPoses} champ(s) de numéro de page`}, écrits en direct — l'avocat n'aura aucune modification suivie à `
        + 'accepter.'
        + (sortDeLAutrePartie.outcome === 'kept_by_copy'
          ? ` Pour garder sur ${PAGES_DE_ZONE[input.type]} le même ${autrePartie === 'footer' ? 'pied de page' : 'en-tête'} qu'avant, `
            + `${NOM_DE_CIBLE[autrePartie]} ${NOM_DE_ZONE[input.type]} a reçu une copie de celui des autres pages.`
          : sortDeLAutrePartie.outcome === 'copy_unverified'
            ? ` ${NOM_DE_CIBLE[autrePartie].replace(/^l'/, 'L\'').replace(/^le /, 'Le ')} ${NOM_DE_ZONE[input.type]} a reçu une copie de `
              + 'celui des autres pages, que la relecture n\'a pas pu comparer à sa source.'
            : '')
        + ` ${pourAnnuler(ecrituresFaites)}`,
      nodeIds: [...nodeIdsEcrits],
    }],
  });

  const sortFinal: SortDeLAutrePartie = sortDeLAutrePartie;
  const gardee = sortFinal.outcome === 'untouched' || sortFinal.outcome === 'kept_by_copy';
  const controle: VerificationCheck = {
    name: 'autre_partie_gardee',
    passed: gardee,
    message: gardee
      ? `${NOM_DE_CIBLE[autrePartie]} montre ce qu'il montrait avant l'écriture (headerFooter.other_part : ${sortFinal.outcome}).`
      : sortFinal.outcome === 'copy_unverified'
        ? `${NOM_DE_CIBLE[autrePartie]} a été copié sur ${PAGES_DE_ZONE[input.type]}, mais la copie n'a pas pu être comparée à sa source (headerFooter.other_part).`
        : `${NOM_DE_CIBLE[autrePartie]} a changé sur ${PAGES_DE_ZONE[input.type]} sans que ce soit demandé (headerFooter.other_part).`,
  };
  // `autreAvant` n'est nul que pour une écriture sur toutes les pages, où l'autre partie n'est jamais concernée.
  const erreurDAutrePartie = sortFinal.outcome === 'untouched' || sortFinal.outcome === 'kept_by_copy' || autreAvant === null
    ? undefined
    : sortFinal.outcome === 'copy_unverified'
      ? erreurDeCopieNonVerifiee(input, sortFinal)
      : erreurDAutrePartieChangee(input, sortFinal, autreAvant);
  // L'écriture demandée est faite : un reçu `ok` devient `partial`, tout autre statut garde le sien et ses erreurs.
  const apresLAutrePartie = avecUnConstat(recu, controle, erreurDAutrePartie);
  if (numerosDePage === undefined) return apresLAutrePartie;
  const zoneVisee = `${NOM_DE_CIBLE[input.target]} ${NOM_DE_ZONE[input.type]} de la section ${input.section_index}`;
  return avecUnConstat(apresLAutrePartie, controleDesNumerosDePage(numerosDePage, zoneVisee, causeDeNonLecture), numerosDePage.outcome === 'conforming' ? undefined : {
    code: numerosDePage.outcome === 'unreadable' ? 'HEADER_FOOTER_PAGE_NUMBER_UNVERIFIED' : 'HEADER_FOOTER_PAGE_NUMBER_DIFFERENT',
    message: messageDesNumerosNonTenus(numerosDePage, zoneVisee, causeDeNonLecture),
  });
}

/**
 * Ce que le retrait d'un numéro de page a fait, dit au modèle. La règle du verbe : la LIGNE qui porte le numéro part
 * entière. Un texte qui partageait cette ligne est donc parti avec lui — le reçu le cite au lieu de dire le reste gardé.
 */
function effetDuRetrait(faits: {
  zoneVisee: string;
  champsRetires: number;
  autresChampsRetires: number;
  /** Le texte, numéros retirés, de chaque paragraphe supprimé ; vide : il ne portait que le numéro. */
  paragraphesRetires: readonly string[];
  paragraphesLaisses: number;
}): string {
  // Les mêmes paragraphes que `page_numbers.removed_paragraphs` cite ; ceux d'après sont comptés.
  const cites = faits.paragraphesRetires.slice(0, CANVAS_REMOVED_PARAGRAPHS_MAX);
  const nonCites = faits.paragraphesRetires.length - cites.length;
  const textesPartis = cites.filter((texte) => texte.length > 0);
  const seuls = cites.length - textesPartis.length;
  return `${faits.champsRetires} champ(s) de numéro de page retiré(s) de ${faits.zoneVisee}`
    + (faits.autresChampsRetires === 0 ? '' : `, et ${faits.autresChampsRetires} autre(s) champ(s) que leurs paragraphes portaient`)
    + ", en direct — l'avocat n'aura aucune modification suivie à accepter."
    + (textesPartis.length === 0 ? '' : ` La ligne du numéro part entière : ce texte est parti avec le numéro — « ${textesPartis.join(' », « ')} » `
      + "(headerFooter.page_numbers.removed_paragraphs). Dis-le à l'avocat ; s'il veut le garder, réécris-le par set_header_footer avec paragraphs.")
    + (seuls === 0 ? '' : ` ${seuls} paragraphe(s) qui ne portait que le numéro est retiré avec lui.`)
    + (nonCites === 0 ? '' : ` ${nonCites} autre(s) paragraphe(s) sont partis avec leur numéro (headerFooter.page_numbers.removed_paragraphs_not_listed) : leur texte n'est pas cité ici, dis à l'avocat que ces lignes sont parties aussi.`)
    + (faits.paragraphesLaisses === 0 ? '' : ` ${faits.paragraphesLaisses} paragraphe(s) qui portaient le numéro sont gardés : headerFooter.warnings dit pourquoi.`)
    + ' Les autres paragraphes de la zone ne sont pas touchés.';
}

const champsEnClair = (champs: readonly CanvasPageNumberField[]): string => (champs.length === 0 ? 'aucun' : champs.map((champ) => INSTRUCTION_DU_NUMERO[champ]).join(', '));

/** Ce que le fichier relu dit des numéros de page de la zone, en un contrôle. */
function controleDesNumerosDePage(numeros: CanvasHeaderFooterPageNumbers, zoneVisee: string, causeDeNonLecture: string | undefined): VerificationCheck {
  return {
    name: 'numeros_de_page_relus',
    passed: numeros.outcome === 'conforming',
    message: numeros.outcome === 'conforming'
      ? `Relu dans le fichier de l'acte : les champs de numéro de page de ${zoneVisee} sont ceux demandés (headerFooter.page_numbers.fields_after : ${champsEnClair(numeros.fields_after ?? [])}).`
      : messageDesNumerosNonTenus(numeros, zoneVisee, causeDeNonLecture),
  };
}

/** Un numéro de page non tenu ou non vérifié, dit au modèle : le champ du reçu, ce qui est lu, quoi dire à l'avocat. */
function messageDesNumerosNonTenus(numeros: CanvasHeaderFooterPageNumbers, zoneVisee: string, causeDeNonLecture: string | undefined): string {
  const demande = numeros.requested === 'set' ? 'la pose' : 'le retrait';
  if (numeros.outcome === 'unreadable') {
    return `set_header_footer a fait ${demande} du numéro de page sur ${zoneVisee}, mais le fichier de l'acte n'a pas pu le confirmer `
      + `(headerFooter.page_numbers.outcome "unreadable") : `
      + (causeDeNonLecture === undefined
        ? "un paragraphe écrit n'y a pas été retrouvé sous son identifiant. "
        : `cause rendue telle quelle : « ${causeDeNonLecture} ». `)
      + "N'annonce pas le numéro de page comme fait : dis à l'avocat de le vérifier à l'écran. Ne rejoue pas l'appel.";
  }
  if (numeros.requested === 'remove') {
    const restants = numeros.fields_after ?? [];
    return restants.length > 0
      ? `set_header_footer n'a pas retiré tous les numéros de page de ${zoneVisee} : le fichier de l'acte en porte encore `
        + `(headerFooter.page_numbers.fields_after : ${champsEnClair(restants)}). headerFooter.warnings dit quel paragraphe n'a pas pu être visé. `
        + 'Dis à l\'avocat que ce numéro reste. Pour le retirer avec sa ligne, réécris la zone entière par set_header_footer en mode "replace".'
      : `set_header_footer a retiré les numéros de page de ${zoneVisee}, mais la zone ne porte plus aucun paragraphe dans le fichier de l'acte : `
        + "une partie sans paragraphe n'est pas valide dans Word. Écris cette zone par set_header_footer (un paragraphe suffit), puis dis à l'avocat ce qui a été fait.";
  }
  return `set_header_footer a fait ${demande} du numéro de page sur ${zoneVisee}, mais le fichier de l'acte ne porte pas ce qui était demandé `
    + `(headerFooter.page_numbers.outcome "different" ; champs lus dans la zone, dans l'ordre : ${champsEnClair(numeros.fields_after ?? [])}). `
    + "Dis à l'avocat ce que la zone porte ; ne rejoue pas l'appel à l'identique, il ajouterait un second paragraphe. Pour reprendre, "
    + 'réécris la zone entière par set_header_footer en mode "replace".';
}

/**
 * La première page distincte d'une section, telle que `sections.list()` la publie APRÈS l'écriture.
 * `null` : la clé n'est pas publiée — « non lisible », jamais « faux » (fait mesuré n° 5).
 */
function premierePageDistincteRelue(sectionsRelues: unknown, rang: number): boolean | null {
  const relue = entrees(sectionsRelues)[rang]?.titlePage;
  return typeof relue === 'boolean' ? relue : null;
}

/** Une zone de première page n'est peinte que si le réglage se relit levé : l'accusé de `setTitlePage` ne le prouve pas. */
function controleDePremierePageRelue(relue: boolean | null, rang: number): VerificationCheck {
  return {
    name: 'premiere_page_distincte_relue',
    passed: relue === true,
    message: relue === true
      ? `Relecture : la première page distincte est activée sur la section ${rang}.`
      : `set_header_footer a écrit une zone de première page, mais la section ${rang} ne se relit pas avec la première page `
        + `distincte activée (lu : ${relue === null ? 'réglage non publié par l\'éditeur' : 'désactivée'}). Tant qu'elle ne l'est pas, `
        + 'cette zone n\'est peinte sur aucune page : ne l\'annonce pas à l\'avocat. Relis la section par canvas_inspect en '
        + 'incluant le domaine "headersFooters" (different_first_page) avant toute autre action.',
  };
}

/**
 * La postcondition : la zone doit se RELIRE avec le texte demandé.
 *
 * Deux preuves exigées ensemble, parce qu'aucune ne suffit :
 *  - `resolve` doit rendre `explicit` — c'est ce qui établit que la section
 *    porte bien sa propre partie et non celle d'une section antérieure ;
 *  - chaque texte demandé doit se retrouver dans les blocs relus. Compter les
 *    blocs ne prouverait rien : une matérialisation ajoute un paragraphe vide,
 *    et un `append` en laisse d'anciens.
 */
function controleDeZoneRelue(
  statutApres: 'explicit' | 'inherited' | 'none',
  relus: readonly Record<string, unknown>[],
  input: SetHeaderFooterInput,
  nodeIdsEcrits: ReadonlySet<string>,
  /** Le texte des paragraphes demandés qui ne portent pas de numéro de page. */
  textesAttendus: readonly string[],
): VerificationCheck {
  const textesRelus = relus.map((bloc) => (chaine(bloc.text) ?? chaine(bloc.textPreview) ?? '').trim());
  const demandes = input.paragraphs?.length ?? 0;
  const manquants = textesAttendus
    .map((texte) => texte.trim())
    .filter((attendu) => !textesRelus.some((relu) => relu === attendu || relu.includes(attendu)));
  const tenu = statutApres === 'explicit' && manquants.length === 0;

  return {
    name: 'zone_relue',
    passed: tenu,
    message: tenu
      ? `Relecture : ${NOM_DE_CIBLE[input.target]} ${NOM_DE_ZONE[input.type]} de la section ${input.section_index} `
        + `porte bien les ${demandes} paragraphe(s) demandés (${relus.length} bloc(s) relu(s), `
        + `${nodeIdsEcrits.size} créé(s) par cet appel).`
      : statutApres !== 'explicit'
        ? `set_header_footer a été annoncé en succès par l'éditeur, mais la relecture rend un slot « ${statutApres} » `
          + `au lieu de « explicit » : ${NOM_DE_CIBLE[input.target]} ${NOM_DE_ZONE[input.type]} n'appartient donc pas `
          + `à la section ${input.section_index} et rien n'est prouvé écrit. Relis le document par canvas_inspect en `
          + 'incluant le domaine "headersFooters" avant toute autre action — ne rejoue pas ce verbe à l\'identique, '
          + 'il poserait un doublon si la première écriture avait bien eu lieu.'
        : `set_header_footer a été annoncé en succès par l'éditeur, mais ${manquants.length} des `
          + `${demandes} paragraphe(s) demandés ne se relisent PAS dans la zone `
          + `(manquant(s) : « ${manquants.map((texte) => extrait(texte)).join(' », « ')} »). Relis le document par `
          + 'canvas_inspect en incluant le domaine "headersFooters" avant toute autre action, et ne repose que ce '
          + 'qui manque — rejouer l\'appel entier dupliquerait ce qui est déjà en place.',
  };
}
